/* =========================================================================
   eAuto Predictive Search - drop-in enhancement
   ----------------------------------------------------------------------------
   File:    assets/eauto-search.js
   Theme:   Warehouse v9-6Mar26 (eauto.co.in)
   Purpose: Replaces the broken/disabled Maestrooo predictive search with a
            fast, relevance-tuned dropdown that:
              1. Calls Shopify's /search/suggest.json API (free, no app cost)
              2. Filters by selected vehicle (VehicleBrand_/VehicleModel_ tags)
              3. Applies Hinglish + synonym normalisation
              4. Falls back to SKU / part-number search when title search fails
              5. Supports keyboard navigation + ARIA + mobile UX

   Safe to remove: drop the <script> tag from layout/theme.liquid. The original
   header.liquid markup will continue to render with no JS attached.

   No external dependencies. ES2017+. Targets evergreen browsers + Safari 13+.
   ========================================================================= */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // CONFIG  (tweak these without touching the rest of the file)
  // ---------------------------------------------------------------------------
  var CONFIG = {
    minChars: 2,              // start searching after N characters
    debounceMs: 180,          // input debounce
    maxResults: 8,            // products in dropdown
    maxSuggestions: 4,        // text query suggestions
    apiUrl: '/search/suggest.json',
    searchPageUrl: '/search',
    storageKey: 'eauto.search.vehicle.v1',
    recentKey: 'eauto.search.recent.v1',
    maxRecent: 5,
    showFitmentBadges: true,  // ROLLBACK: set false to hide "Fits your bike" badges

    // CSS selectors of the EXISTING Warehouse markup we hook into.
    selectors: {
      form:        '.header__search-bar-wrapper .search-bar',
      input:       '.header__search-bar-wrapper .search-bar__input',
      resultsBox:  '.header__search-bar-wrapper .search-bar__results',
      resultsInner:'.header__search-bar-wrapper .search-bar__results-inner',
      skeleton:    '.header__search-bar-wrapper .skeleton-container',
      menuWrap:    '.header__search-bar-wrapper .search-bar__menu-wrapper',
      vehicleMount:'#eauto-vehicle-filter' // injected by the snippet
    }
  };

  // ---------------------------------------------------------------------------
  // EMPTY-STATE CATEGORIES - shown when search is open with no query
  // ---------------------------------------------------------------------------
  // Quick-access chips for first-time discovery. Ranked by REAL search volume
  // pulled from Shopify ShopifyQL (`searches` table, search_results_were_returned
  // = true, last 90d). Refresh annually or whenever search patterns shift.
  //
  // Top 6 by aggregated category-prefix search volume (May 2026 data):
  //   carburetor family ~11K, speedometer ~10K, side panel ~10K,
  //   headlight ~8K, chain & sprocket ~7K, silencer ~4.5K
  //
  // Each chip uses an inline SVG icon (not emoji) for a cleaner, more
  // professional look. Click navigates to /search?q=<term> via buildSearchUrl
  // so vehicle filter tags are preserved if one is selected.
  function svgCarburetor() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 3l2 3 2-3"/><rect x="6" y="7" width="12" height="13" rx="1.5"/><line x1="6" y1="11" x2="18" y2="11"/><circle cx="12" cy="15" r="1.8" fill="currentColor"/><line x1="9" y1="20" x2="9" y2="22"/><line x1="15" y1="20" x2="15" y2="22"/></svg>';
  }
  function svgSpeedometer() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 16a9 9 0 1 1 18 0"/><line x1="12" y1="16" x2="7.5" y2="10.5"/><circle cx="12" cy="16" r="1.6" fill="currentColor"/><line x1="4" y1="14" x2="5.5" y2="14"/><line x1="20" y1="14" x2="18.5" y2="14"/><line x1="12" y1="6" x2="12" y2="7.5"/></svg>';
  }
  function svgHeadlight() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="8.5" cy="12" r="5"/><circle cx="8.5" cy="12" r="2" fill="currentColor"/><line x1="14" y1="12" x2="22" y2="12"/><line x1="14" y1="8.5" x2="20.5" y2="6"/><line x1="14" y1="15.5" x2="20.5" y2="18"/></svg>';
  }
  function svgSidePanel() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8 Q5 4 9 4 L17 4 Q20 4 20 7 L20 17 Q20 20 17 20 L9 20 Q5 20 5 16 Z"/><line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="13" x2="13" y2="13"/></svg>';
  }
  function svgChain() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.6" fill="currentColor"/><line x1="12" y1="3" x2="12" y2="5.5"/><line x1="12" y1="18.5" x2="12" y2="21"/><line x1="3" y1="12" x2="5.5" y2="12"/><line x1="18.5" y1="12" x2="21" y2="12"/><line x1="5.5" y1="5.5" x2="7.5" y2="7.5"/><line x1="16.5" y1="16.5" x2="18.5" y2="18.5"/><line x1="5.5" y1="18.5" x2="7.5" y2="16.5"/><line x1="16.5" y1="7.5" x2="18.5" y2="5.5"/></svg>';
  }
  function svgSilencer() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h11l6-3v6l-6-3"/><line x1="6" y1="9" x2="6" y2="15"/><line x1="9" y1="9" x2="9" y2="15"/></svg>';
  }
  function svgClutch() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="4" x2="12" y2="9"/><line x1="12" y1="15" x2="12" y2="20"/><line x1="4" y1="12" x2="9" y2="12"/><line x1="15" y1="12" x2="20" y2="12"/></svg>';
  }
  function svgFuelTank() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 6 Q5 4 7 4 L17 4 Q19 4 19 6 L19 18 Q19 20 17 20 L7 20 Q5 20 5 18 Z"/><line x1="9" y1="8" x2="15" y2="8"/><circle cx="12" cy="13" r="2"/></svg>';
  }
  function svgHandlebar() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><rect x="2" y="9" width="4" height="6" rx="1"/><rect x="18" y="9" width="4" height="6" rx="1"/><line x1="12" y1="12" x2="12" y2="20"/></svg>';
  }
  function svgVisor() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 14 Q12 5 21 14 L21 17 L3 17 Z"/><line x1="6" y1="14" x2="18" y2="14"/></svg>';
  }
  function svgEngine() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="8" width="16" height="10" rx="1.5"/><line x1="8" y1="8" x2="8" y2="4"/><line x1="16" y1="8" x2="16" y2="4"/><line x1="8" y1="12" x2="8" y2="14"/><line x1="16" y1="12" x2="16" y2="14"/><line x1="12" y1="12" x2="12" y2="14"/></svg>';
  }
  function svgAllParts() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>';
  }
  // Motorcycle icon for the vehicle chip. Material Icons "two_wheeler"
  // SVG path (Apache 2.0). Heavy filled silhouette designed for small UI
  // sizes - readable at 18px.
  function svgMotorcycle() {
    return '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">'
      + '<path d="M19.44 9.03L15.41 5H11v2h3.59l2 2H5c-2.8 0-5 2.2-5 5s2.2 5 5 5c2.46 0 4.45-1.69 4.9-4h1.65l2.77-2.77c-.21.54-.32 1.14-.32 1.77 0 2.8 2.2 5 5 5s5-2.2 5-5c0-2.65-1.97-4.77-4.56-4.97zM7.82 15C7.4 16.15 6.28 17 5 17c-1.63 0-3-1.37-3-3s1.37-3 3-3c1.28 0 2.4.85 2.82 2H5v2h2.82zM19 17c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>'
      + '</svg>';
  }
  var EMPTY_STATE_CATEGORIES = [
    { label: 'Carburetor',     svg: svgCarburetor,  q: 'carburetor' },
    { label: 'Speedometer',    svg: svgSpeedometer, q: 'speedometer' },
    { label: 'Side panel',     svg: svgSidePanel,   q: 'side panel' },
    { label: 'Headlight',      svg: svgHeadlight,   q: 'headlight' },
    { label: 'Chain sprocket', svg: svgChain,       q: 'chain sprocket' },
    { label: 'Silencer',       svg: svgSilencer,    q: 'silencer' },
    { label: 'Clutch',         svg: svgClutch,      q: 'clutch' },
    { label: 'Fuel tank',      svg: svgFuelTank,    q: 'fuel tank' },
    { label: 'Handle / grips', svg: svgHandlebar,   q: 'handle' },
    { label: 'Visor',          svg: svgVisor,       q: 'visor' },
    { label: 'Engine parts',   svg: svgEngine,      q: 'engine' },
    { label: 'Shock absorber', svg: svgClutch,      q: 'shock absorber' }
  ];

  // Curated "popular searches" shown in the empty-state panel before the user
  // types. Derived from the top zero-result-rescued groups in the 90-day GSC/
  // ShopifyQL data (SD-SYNONYM-PRIORITY) - all GraphQL-verified to return
  // products (2026-06-29). Canonical spellings only, so a click lands on real
  // results, not a typo.
  var TRENDING_SEARCHES = [
    'pulsar',
    'apache',
    'brake shoe',
    'chain sprocket',
    'mudguard',
    'seat cover'
  ];

  // ---------------------------------------------------------------------------
  // SYNONYM + HINGLISH MAP
  // ---------------------------------------------------------------------------
  // When a user types the LEFT side, we also search for everything on the RIGHT.
  // Keep entries lowercase; entries are matched on whole-word boundary.
  // Add to this list freely - order does not matter.
  var SYNONYMS = {
    'brake shoe':          ['brake liner', 'brake pad', 'brake lining'],
    'brake liner':         ['brake shoe', 'brake pad'],
    'brake pad':           ['brake shoe', 'disc pad'],
    'disc pad':            ['brake disc pad', 'brake pad'],
    'clutch plate':        ['clutch friction plate', 'clutch disc', 'pressure plate'],
    'pressure plate':      ['clutch plate', 'clutch cover'],
    // 'carburetor' must be first in its own values array - bestSearchUrlQuery
    // uses values[0] as the canonical, and we want /search?q=carburetor (US
    // spelling), not /search?q=carburettor.
    'carburetor':          ['carburetor', 'carburettor', 'carb', 'carby'],
    'carburettor':         ['carburetor', 'carb'],
    'carb':                ['carburetor', 'carburettor'],
    // Canonical-first: 'tyre' is the Indian-English form used in product
    // titles. URL-rewriting to 'tire' returned fog-lamp products (wrong match).
    'tyre':                ['tyre', 'tire', 'tayar'],
    'tire':                ['tyre'],
    // Canonical-first (see comment on 'carburetor' above). The /search rescue
    // re-runs bestSearchUrlQuery on the full URL - if canonical isn't first,
    // the bike prefix or category chip word gets corrupted.
    'petrol tank':         ['petrol tank', 'fuel tank', 'gas tank'],
    'fuel tank':           ['fuel tank', 'petrol tank'],
    'headlight':           ['headlight', 'head light', 'head lamp', 'headlamp'],
    'head light':          ['head light', 'headlight', 'headlamp'],
    'headlamp':            ['headlight'],
    'indicator':           ['blinker', 'turn signal', 'winker'],
    'blinker':             ['indicator', 'turn signal'],
    'shocker':             ['shock absorber', 'suspension', 'rear shock'],
    // Canonical-first: Shopify products are titled "Shock Absorber".
    // URL-rewriting to "shocker" matched a product whose TITLE contains
    // "Shocker Length 39 cm" but most shock-absorber products don't have
    // that string in their title. Reported QA bug: Activa 5G + shock
    // absorber chip -> zero results because URL went to ".../shocker".
    'shock absorber':      ['shock absorber', 'shocker', 'suspension'],
    'mileage':             ['fuel efficiency', 'kmpl'],
    'engine oil':          ['motor oil', 'lubricant', '4t oil'],
    'motor oil':           ['engine oil'],
    'battery':             ['btry', 'bttry', 'cell'],
    'spark plug':          ['plug', 'sparkplug'],
    'self start':          ['starter motor', 'self motor', 'starter'],
    'starter motor':       ['self start', 'self motor'],
    'magnet':              ['magneto', 'stator', 'flywheel magnet'],
    'magneto':             ['magnet', 'stator'],
    'horn':                ['horn assembly', 'hooter'],
    'mirror':              ['rear view mirror', 'rvm', 'side mirror'],
    'side mirror':         ['mirror', 'rear view mirror'],
    'mudguard':            ['mud guard', 'fender'],
    // Canonical-first. Products are titled "Silencer ..." in this catalog.
    'silencer':            ['silencer', 'exhaust', 'muffler'],
    'exhaust':             ['exhaust', 'silencer'],
    'chain sprocket':      ['chain sprocket', 'chain kit', 'sprocket set', 'chain set'],
    'chain set':           ['chain sprocket', 'chain kit'],
    'air filter':          ['air cleaner', 'filter element'],
    'oil filter':          ['filter cartridge'],
    'cdi':                 ['ignition unit', 'cdi unit'],
    'ecu':                 ['electronic control unit', 'engine control'],
    'piston':              ['piston kit', 'piston assembly'],
    'piston kit':          ['piston', 'piston assembly', 'block kit'],
    'block kit':           ['piston kit', 'cylinder kit'],
    'cylinder kit':        ['block kit', 'piston kit'],
    'kick lever':          ['kick starter', 'kick'],
    'foot rest':           ['footrest', 'foot peg', 'pillion footrest'],
    'seat cover':          ['seat', 'seat assembly'],
    // Canonical-first - products are titled "Speedometer ...".
    // Without 'speedometer' first, the chip click URL became /search?q=...+speedo
    // and the /search rescue couldn't recover. Bug caught by audit 2026-05-14.
    'speedometer':         ['speedometer', 'speedo', 'speed meter', 'meter'],
    'meter':               ['speedometer', 'instrument cluster'],
    'tail light':          ['tail lamp', 'rear lamp', 'rear light'],
    'tail lamp':           ['tail light'],
    'number plate':        ['nameplate', 'reg plate'],
    'wheel':               ['rim', 'alloy wheel'],
    'rim':                 ['wheel', 'alloy'],
    'bulb':                ['lamp', 'light bulb'],
    'wiring harness':      ['wiring', 'main wire', 'harness'],
    // Canonical-first for bike-name keys. Without this, the /search rescue
    // mangled the bike prefix - e.g. "Hero Splendor Pro speedometer" ->
    // "Hero Splendor Plus Pro speedometer", and "Bajaj Pulsar 150 carburetor"
    // -> "Bajaj Pulsar 150 150 carburetor". Bug caught by audit 2026-05-14.
    'pulsar':              ['pulsar', 'pulsar 150', 'pulsar 180', 'pulsar 220'],
    'activa':              ['activa', 'activa 5g', 'activa 6g', 'activa 125', 'activa 3g'],
    'splendor':            ['splendor', 'splendor plus', 'splendor ismart', 'splender'],
    'splender':            ['splendor'],
    'royal enfield':       ['royal enfield', 're', 'bullet', 'classic 350'],
    're':                  ['royal enfield', 'bullet'],
    'bullet':              ['royal enfield', 'classic 350', 'bullet 350'],

    // ---- TYPOS + HINGLISH observed in eAuto's own search analytics (Feb-May 2026)
    // Every entry below has been seen in real customer queries. See
    // SEARCH-ANALYTICS-REPORT.md for the data backing each one.
    'break':               ['brake'],                          // most common typo of all
    'back break':          ['rear brake', 'back brake'],
    'pichhle break':       ['rear brake', 'back brake'],       // Hindi phonetic
    'pichle break':        ['rear brake', 'back brake'],
    'corporator':          ['carburetor', 'carburettor', 'carb'],
    'corparator':          ['carburetor'],
    'kabretor':            ['carburetor', 'carb'],
    'karbretor':           ['carburetor', 'carb'],
    'waring':              ['wiring', 'wire harness'],
    'chine spocet':        ['chain sprocket'],
    'spocet':              ['sprocket', 'chain sprocket'],
    'sprocet':             ['sprocket', 'chain sprocket'],
    'cilencer':            ['silencer'],
    'silencor':            ['silencer'],
    'mettar':              ['meter', 'speedometer'],           // Hinglish phonetic
    'teal light':          ['tail light', 'tail lamp'],
    'back light':          ['tail light', 'tail lamp', 'rear light'],
    'rear light':          ['tail light', 'tail lamp'],
    'pulsor':              ['pulsar'],
    'pulsr':               ['pulsar'],
    'fasino':              ['fascino'],
    'shokeer':             ['shocker', 'shock absorber'],
    'shoker':              ['shocker', 'shock absorber'],
    'front guard':         ['front mudguard', 'front fender'],
    'rear guard':          ['rear mudguard', 'rear fender'],
    'karzma':              ['karizma'],
    'splendar':            ['splendor', 'splendor plus'],

    // ---- TIER 2 typos observed in rows 100-500 of the same analytics pull ----
    // Pulsar variants - extremely common across queries
    'pulser':              ['pulsar'],
    'palsar':              ['pulsar'],
    'palsa':               ['pulsar'],          // 637 zero-result searches/qtr
    'palser':              ['pulsar'],          // 160
    'palsu':               ['pulsar'],          // 158
    'pala':                ['pulsar'],          // 158
    'palse':               ['pulsar'],          // 88
    'pursa':               ['pulsar'],          // 42
    'pusa':                ['pulsar'],          // 96
    'puk':                 ['pulsar'],          // 80
    'pls':                 ['pulsar'],          // 90
    'pal':                 ['pulsar'],          // 290 (high-volume partial)
    'palsure':             ['pulsar'],
    'pulsar150':           ['pulsar 150'],      // 227 (no-space variant)
    'pulsar180':           ['pulsar 180'],      // 72
    'pulsar220':           ['pulsar 220'],      // 96
    'pulsar125':           ['pulsar 125'],      // 83
    'mt15':                ['mt 15'],           // 743
    'mt1':                 ['mt 15'],           // 634 (partial)
    'cd100':               ['cd 100'],          // 665
    'cd10':                ['cd 100'],          // 148
    'ns125':               ['ns 125'],          // 450
    'r15v3':               ['r15 v3'],          // 422
    'r15v2':               ['r15 v2'],          // 378
    'r15v4':               ['r15 v4'],          // 272
    'r15v1':               ['r15 v1'],          // 68
    'xl100':               ['xl 100'],          // 143
    'xl1':                 ['xl 100'],          // 120
    'cbr250':              ['cbr 250'],         // 120
    'cbr150r':             ['cbr 150r'],        // 86
    'cbr250r':             ['cbr 250r'],        // 72
    'cbr15':               ['cbr 150'],         // 89
    'cbr25':               ['cbr 250'],         // 86
    'cbr150':              ['cbr 150'],
    'sp125':               ['sp 125'],          // 87
    'sp12':                ['sp 125'],          // 80
    'sp160':               ['sp 160'],          // 48
    'sp16':                ['sp 160'],          // 38
    'discover100':         ['discover 100'],    // 73
    'discover125':         ['discover 125'],    // 44
    'access125':           ['access 125'],      // 70
    'xcd125':              ['xcd 125'],         // 70
    'xcd135':              ['xcd 135'],         // 38
    'xcd12':               ['xcd 125'],         // 55
    'cb350':               ['cb 350'],          // 54
    'cb200':               ['cb 200'],          // 40
    'cb200x':              ['cb 200x'],         // 38
    'rr310':               ['rr 310'],          // 74
    'rr31':                ['rr 310'],          // 44
    'ns400':               ['ns 400'],
    'ns40':                ['ns 400'],          // 42
    'ns200':               ['ns 200'],
    'ns160':               ['ns 160'],
    'activa125':           ['activa 125'],      // 54
    'hondashine':          ['honda shine'],     // 38
    'passionpro':          ['passion pro'],     // 45
    'fzv1':                ['fz v1'],           // 74
    'fzv2':                ['fz v2'],           // 153
    'fzv3':                ['fz v3'],           // 150
    'fzsv3':               ['fzs v3'],          // 42
    'pulser150':           ['pulsar 150'],      // 58
    'palser 1':            ['pulsar 1'],
    'palser 15':           ['pulsar 15'],
    'palser 150':          ['pulsar 150'],      // 52
    'bajaj pal':           ['bajaj pulsar'],    // 233 (partial)
    'bajaj pals':          ['bajaj pulsar'],    // 78
    'bajaj palsa':         ['bajaj pulsar'],    // 55
    'mt 15':               ['mt 15', 'mt15', 'yamaha mt 15'],
    'airfilter':           ['air filter'],      // 58
    'oilfilter':           ['oil filter'],
    'crankcase':           ['crank case'],      // 46
    'backlight':           ['tail light', 'back light'], // 106
    'herohonda':           ['hero honda'],      // 168
    'herohon':             ['hero honda'],      // 154
    'herohond':            ['hero honda'],      // 128
    // ---- Hinglish / phonetic brand misspellings ----
    'yahama':              ['yamaha'],
    'yema':                ['yamaha'],          // 134
    'yemah':               ['yamaha'],          // 78
    'yana':                ['yamaha'],          // 56
    'yma':                 ['yamaha'],          // 52
    'sux':                 ['suzuki'],          // 232
    'suju':                ['suzuki'],          // 126
    'suj':                 ['suzuki'],          // 136
    'sujuk':               ['suzuki'],          // 46
    'suu':                 ['suzuki'],          // 40
    'suk':                 ['suzuki'],          // 44
    'homd':                ['honda'],           // 96
    'hend':                ['honda'],
    'hende':               ['honda'],
    'bja':                 ['bajaj'],           // 234
    'bjaja':               ['bajaj'],           // 53
    'apch':                ['apache'],          // 360
    'apc':                 ['apache'],          // 352
    'appac':               ['apache'],          // 262
    'apchi':               ['apache'],          // 86
    'aapa':                ['apache'],          // 64
    'aapac':               ['apache'],          // 40
    'aph':                 ['apache'],          // 38
    'appch':               ['apache'],          // 68
    // ---- Hinglish / phonetic part misspellings ----
    'sok':                 ['shocker', 'shock absorber'], // 167
    'soka':                ['shocker'],         // 80
    'sokar':               ['shocker'],         // 86
    'soker':               ['shocker'],         // 52
    'shokar':              ['shocker', 'shock absorber'],
    'vai':                 ['visor'],           // 142
    'wis':                 ['visor'],           // 132
    'viz':                 ['visor'],           // 88
    'vij':                 ['visor'],           // 46
    'wiser':               ['visor'],           // 156
    'wizer':               ['visor'],           // 56
    'mita':                ['meter', 'speedometer'], // 170
    'mite':                ['meter'],           // 111
    'meet':                ['meter'],           // 88
    'galm':                ['glamour'],         // 90
    'galam':               ['glamour'],         // 56
    'galem':               ['glamour'],         // 49
    'gale':                ['glamour'],         // 67
    'gle':                 ['glamour'],         // 101
    'glom':                ['glamour'],         // 108
    'glomo':               ['glamour'],         // 54
    'glm':                 ['glamour'],         // 56
    'glem':                ['glamour'],         // 68
    'glamou':              ['glamour'],
    'megn':                ['magneto', 'magnet'], // 78
    'megne':               ['magneto'],         // 70
    'pessan':              ['passion'],
    'passan':              ['passion'],         // 78
    'pess':                ['passion'],         // 107
    'pesa':                ['passion'],         // 42
    'avan':                ['avenger'],         // 166
    'avang':               ['avenger'],         // 108
    'baik':                ['bike'],
    'breck':               ['brake'],           // 77
    'brek':                ['brake'],           // 186
    'brec':                ['brake'],           // 74
    'brke':                ['brake'],           // 176 - letter-omission
    'brak':                ['brake'],           // letter-omission
    'brakke':              ['brake'],           // letter-doubling
    'braek':               ['brake'],           // letter-transposition
    'break p':             ['brake pad'],       // 172 - truncated
    'break l':             ['brake lever'],     // 162 - truncated
    'break pa':            ['brake pad'],       // 150
    'break li':            ['brake liner'],     // 92
    'break pad':           ['brake pad'],       // 134 - fix the "break" spelling
    'break le':            ['brake lever'],     // 64
    'break lever':         ['brake lever'],     // 70
    'break ca':            ['brake cable'],     // 74
    'break light':         ['brake light', 'tail light'],  // 56
    'front break':         ['front brake'],     // 94
    'front bre':           ['front brake'],     // 80
    'front brea':          ['front brake'],     // 62
    'disc break':          ['disc brake'],      // 70
    'disk break':          ['disc brake'],      // 56
    'disc bre':            ['disc brake'],      // 38
    'disc brea':           ['disc brake'],      // 38
    'chain cover':         ['chain cover', 'chain guard'],  // 442 canonical
    'chain co':            ['chain cover'],     // 414 - truncated
    'chain cov':           ['chain cover'],     // 308
    'chain cove':          ['chain cover'],     // 145
    'oil fil':             ['oil filter'],      // 214
    'oil filt':            ['oil filter'],      // 86
    'oil filte':           ['oil filter'],      // 40
    'rear mud':            ['rear mudguard'],   // 192
    'rear mudg':           ['rear mudguard'],   // 90
    'rear mudgu':          ['rear mudguard'],   // 44
    'rear mudguard':       ['rear mudguard'],   // 134
    'mud gu':              ['mud guard', 'mudguard'],  // 186
    'mud g':               ['mud guard', 'mudguard'],  // 244
    'mud gua':             ['mud guard'],       // 122
    'mud guar':            ['mud guard'],       // 78
    'tvs raid':            ['tvs raider'],      // 214 - truncated
    'tvs raide':           ['tvs raider'],      // 114
    'raide':               ['raider'],          // 108
    'tvs starci':          ['tvs starcity'],    // 112
    'tvs starcit':         ['tvs starcity'],    // 50
    'starcity':            ['star city', 'tvs starcity'],  // 80
    'tvs ronin':           ['tvs ronin'],       // 122
    'tvs roni':            ['tvs ronin'],       // 56
    'ronin':               ['tvs ronin'],       // 108
    'roni':                ['tvs ronin'],       // 68
    'suzuki max100':       ['suzuki max 100'],  // 154
    'max100':              ['max 100'],         // 128
    'bajaj freedom':       ['bajaj freedom 125', 'freedom'],  // 116
    'speedometer cable':   ['speedometer cable', 'meter cable'],  // 111
    // Speedometer "vowel-drop" typos - common when typing on mobile (auto-
    // complete drops the 'eo' / 'eter' from the middle). Not catchable by
    // Levenshtein d=1 (too many chars missing).
    'speedmtr':            ['speedometer'],
    'speedmter':           ['speedometer'],
    'spdmtr':              ['speedometer'],
    'sppdmtr':             ['speedometer'],
    'spdometer':           ['speedometer'],
    'speedmeter':          ['speedometer'],
    'shogun':              ['suzuki shogun'],  // model - not in our dict before
    'speedometer cab':     ['speedometer cable', 'meter cable'],  // 84
    'speedometer cabl':    ['speedometer cable'],  // 53
    'cd100 ss':            ['cd 100 ss'],       // 110
    'cd100 s':             ['cd 100 s'],        // 42
    'cd100s':              ['cd 100 s'],        // 40
    'chain se':            ['chain set', 'chain sprocket'],  // 110
    'side mirr':           ['side mirror'],     // 126
    'side mirro':          ['side mirror'],     // 86
    'side pen':            ['side panel'],      // 88
    'side pena':           ['side panel'],      // 66
    'front pane':          ['front panel'],     // 66
    'front panel':         ['front panel'],     // 132
    'leg gu':              ['leg guard', 'crash guard'],  // 132
    'saree':               ['saree guard'],     // 100
    'saree g':             ['saree guard'],     // 50
    'crash':               ['crash guard'],     // 142
    'crash g':             ['crash guard'],     // 46
    'crash gu':            ['crash guard'],     // 38
    'fender':              ['fender', 'mudguard'],  // 124
    'rear fender':         ['rear fender'],     // 78
    'rear fen':            ['rear fender'],     // 56
    'front fender':        ['front fender'],    // 50
    'front fen':           ['front fender'],    // 38
    'swing':               ['swing arm'],       // 86
    'swinga':              ['swing arm'],       // 60
    'swingar':             ['swing arm'],       // 60
    'swingarm':            ['swing arm'],       // 100
    'swing ar':            ['swing arm'],       // 207
    'swing arm bush':      ['swing arm bush'],  // 43
    // Note: "canonical -> canonical" no-op entries removed. If a term has
    // genuine ambiguity (e.g. mud guard / mudguard / fender) it should map to
    // its alternate form, not to itself. Pure self-mappings don't help search.
    'tale':                ['tail'],            // 52
    'hade':                ['headlight'],       // 70
    'hadl':                ['headlight'],       // 80
    'hadli':               ['headlight'],       // 64
    'hedl':                ['headlight'],       // 168
    'hedli':               ['headlight'],       // 146
    'hedlig':              ['headlight'],       // 66
    'said':                ['side'],            // (e.g. 'said panel' -> 'side panel')
    'madg':                ['mudguard'],        // 124
    'madga':               ['mudguard'],        // 72
    'madgar':              ['mudguard'],        // 40
    'mud gaurd':           ['mud guard', 'mudguard'],
    'corbu':               ['carburetor'],
    'carborate':           ['carburetor'],      // 38
    'carborater':          ['carburetor'],      // 74
    'salen':               ['silencer'],        // 58
    'saile':               ['silencer'],        // 72
    'solen':               ['silencer'],        // 44
    'clach':               ['clutch'],          // 76
    'tail pannel':         ['tail panel'],
    'tail panal':          ['tail panel'],
    'tail pana':           ['tail panel'],
    'tail pann':           ['tail panel'],
    'side penal':          ['side panel'],
    'said panel':          ['side panel'],
    'said penal':          ['side panel'],
    'plti':                ['platina'],
    'palti':               ['platina'],         // 122
    'paltin':              ['platina'],         // 78
    'plant':               ['platina'],         // 57
    'palat':               ['platina'],         // 56
    'pulser 150':          ['pulsar 150'],
    'rear shoker':         ['rear shocker', 'rear shock absorber'],
    'front shoker':        ['front shocker', 'front shock absorber'],
    'sailen':              ['silencer'],
    // Splendor variants
    'speldor':             ['splendor'],
    'spendor':             ['splendor'],
    // Meter / speedometer variants
    'mitar':               ['meter', 'speedometer'],
    'miter':               ['meter', 'speedometer'],
    'metar':               ['meter', 'speedometer'],
    'metre':               ['meter', 'speedometer'],
    // Carburetor variants
    'carborator':          ['carburetor', 'carb'],
    'carbutator':          ['carburetor', 'carb'],
    'carburator':          ['carburetor'],
    // Tank variants
    'fule tank':           ['fuel tank', 'petrol tank'],
    'fiul tank':           ['fuel tank', 'petrol tank'],
    'tenk':                ['tank'],
    'tannk':               ['tank'],
    'tanki':               ['tank', 'fuel tank'],         // Hinglish ()
    'peteol':              ['petrol'],
    // Clutch variants
    'cluch':               ['clutch'],
    'cluch plaite':        ['clutch plate'],
    'cluch ensemble':      ['clutch assembly'],
    // Headlight variants
    'hade light':          ['headlight', 'head lamp'],
    // Tail light variants
    'tay light':           ['tail light', 'tail lamp'],
    'tale light':          ['tail light', 'tail lamp'],
    // Chain sprocket
    'chain sproket':       ['chain sprocket'],
    'sproket':             ['sprocket', 'chain sprocket'],
    // Hinglish single-word part names
    'tayar':               ['tyre', 'tire'],              // 
    'vaijar':              ['visor'],                     // 
    'jhula':               ['shock absorber', 'rear suspension', 'rear shock'], // 
    'haness':              ['harness', 'wiring harness'],
    // Brand / model name misspellings
    'mestro':              ['maestro'],
    'phoneix':             ['phoenix'],
    'achiver':             ['achiever'],
    'yahmaha':             ['yamaha'],
    'apahche':             ['apache'],
    // Misc
    'loack':               ['lock'],
    'frent':               ['front'],
    // -----------------------------------------------------------------------
    // Weekly typo-mining backlog (consolidated 2026-06-29) - all GraphQL-verified
    // HIGH-confidence corrections from weekly-search-health reports 2026-05-15
    // through 2026-06-22. Word-boundary matched, so truncation keys (e.g.
    // 'hf du', 'body ki') never collide with the full term. LOW-confidence and
    // catalog-gap items were intentionally excluded.
    'tail pan':            ['tail panel'],
    'tail pane':           ['tail panel'],
    'tail panne':          ['tail panel'],
    'tale pane':           ['tail panel'],
    'chain spo':           ['chain sprocket'],
    'chain spoc':          ['chain sprocket'],
    'chain spock':         ['chain sprocket'],
    'front fe':            ['front fender'],
    'hf du':               ['hf deluxe'],
    'hf dul':              ['hf deluxe'],
    'hf dulex':            ['hf deluxe'],
    'hfd':                 ['hf deluxe'],
    'hfdelu':              ['hf deluxe'],
    'airfil':              ['air filter'],
    'bsja':                ['bajaj'],
    'bajaj150':            ['bajaj pulsar 150'],
    'pllosr135':           ['pulsar 135'],
    'ntro':                ['ntorq'],
    'tvs ntro':            ['tvs ntorq', 'ntorq'],
    'raya':                ['ray', 'yamaha ray'],
    'full bo':             ['full body'],
    'body ki':             ['body kit'],
    'dio bod':             ['dio body'],
    'carbato':             ['carburetor', 'carb'],
    'duke 25':             ['duke 250'],
    'magwh':               ['mag wheel', 'alloy wheel'],
    'solend':              ['silencer'],
    'odome':               ['speedometer', 'meter'],
    'tvs xl1':             ['tvs xl 100', 'xl 100'],
    'appa':                ['apache'],
    'appchi':              ['apache'],
    'suzuki max100r':      ['suzuki max 100'],
    'fzsv2':               ['fzs v2', 'fz v2'],
    'pletin':              ['platina'],
    'hero hunker':         ['hero hunk'],
    'r15v2m':              ['r15 v2'],
    'diskb':               ['disc brake'],
    'kon set':             ['cone set'],
    'gixer':               ['gixxer'],
    'honda deo':           ['honda dio'],
    'back bre':            ['rear brake', 'back brake'],
    'herohan':             ['hero honda'],
    'fz25':                ['fz 250'],
    'wiz':                 ['visor'],
    'suzuki gs15':         ['suzuki gs 150r'],
    'rtr160':              ['apache rtr 160'],
    // Bare 'shock' had no mapping - products are titled "Shock Absorber", so
    // map it to the canonical term for better ranking + a precise "See all" URL.
    'shock':               ['shock absorber', 'shocker'],
    // Full-word misspellings found in exhaustive testing (2026-06-29). The dict
    // already had the short truncations (salen, madg, hedl) but missed these
    // longer typos, which don't prefix-match the correct product word.
    'salencer':            ['silencer'],
    'headlite':            ['headlight', 'head light'],
    'hedlight':            ['headlight', 'head light'],
    'madguard':            ['mudguard', 'mud guard'],
    'spedo':               ['speedometer', 'meter'],
    'mirar':               ['mirror', 'side mirror'],
    'magw':                ['mag wheel', 'alloy wheel'],
    'pleti':               ['platina']
  };

  // ---------------------------------------------------------------------------
  // POPULAR VEHICLES (filter chip - brand / model dropdown)
  // ---------------------------------------------------------------------------
  // Every entry below corresponds to a real `VehicleModel_<X>` tag used on
  // products in the store (verified against the live Shopify collection rules
  // on 2026-05-12). Models within a brand are ordered roughly by search
  // demand from GSC + by collection product count.
  //
  // Adding a model here will work as long as the tag `VehicleModel_<exact name>`
  // exists on at least one product. Removing one only hides it from the chip;
  // existing tags on products are untouched.
  var VEHICLES = {
    'Bajaj': [
      'Pulsar 125', 'Pulsar 135', 'Pulsar 150', 'Pulsar 160', 'Pulsar 180', 'Pulsar 220', 'Pulsar NS 200',
      'Avenger', 'Avenger 220',
      'Platina', 'CT 100', 'Boxer', 'Caliber', 'Dominar 400',
      'Discover 100', 'Discover 125', 'Discover 135', 'Discover 150',
      'XCD 125', 'XCD 135', 'Vikrant V15', 'Chetak'
    ],
    'Hero': [
      'Splendor', 'Splendor Plus', 'Splendor Pro', 'Super Splendor', 'Splendor iSmart',
      'Passion', 'Passion Pro', 'Passion Plus', 'Passion X Pro',
      'Glamour', 'HF Deluxe', 'HF Dawn',
      'CBZ Xtreme', 'Hunk', 'Karizma', 'Karizma ZMR',
      'Achiever', 'CD Deluxe', 'CD Dawn', 'CD 100',
      'Maestro', 'Pleasure', 'Impulse', 'Xpulse', 'Xtreme',
      'Ambition', 'Duet', 'Ignitor'
    ],
    'Honda': [
      'Activa', 'Activa 3G', 'Activa 4G', 'Activa 5G', 'Activa 6G', 'Activa 125',
      'Shine', 'CB Shine', 'Shine SP', 'SP 125',
      'Unicorn', 'Unicorn 150', 'Unicorn 160',
      'Stunner', 'Hornet', 'Hornet 160',
      'Dio', 'Aviator', 'Grazia', 'Livo', 'Navi', 'Eterno',
      'CB Trigger', 'CB Twister',
      'CBR 150', 'CBR 250',
      'Dream Yuga', 'Dream Neo', 'CD 110 Dream'
    ],
    'TVS': [
      'Apache 150', 'Apache RTR 160', 'Apache RTR 180', 'Apache RTR 200',
      'XL Super', 'XL 100',
      'Jupiter', 'Ntorq 125', 'Star City', 'Raider 125', 'Radeon',
      'Phoenix', 'Victor', 'Sport', 'Flame', 'Wego', 'Scooty Pep', 'Centra', 'Streak', 'Zest', 'Jive', 'Max 4R'
    ],
    'Yamaha': [
      'RX 100', 'RX 135',
      'FZ', 'FZ-S', 'FZ V2', 'FZ V3', 'FZ 250', 'Fazer',
      'R15', 'R15 V2', 'R15 V3', 'MT 15',
      'Fascino', 'Ray ZR', 'Saluto',
      'Crux', 'Gladiator', 'Libero', 'Alba', 'Enticer', 'YBR', 'YBX',
      'SZ', 'SZ-R', 'SZ-RR', 'Alpha'
    ],
    'Suzuki': [
      'Access 125', 'Gixxer', 'Gixxer SF', 'GS 150R',
      'Hayate', 'Intruder', 'Lets', 'Samurai',
      'Slingshot', 'Swish', 'Burgman', 'Avenis',
      'AX 100', 'Max 100', 'Fiero', 'Zeus'
    ],
    'Royal Enfield': [
      'Classic 350', 'Bullet 350', 'Bullet 500',
      'Thunderbird', 'Meteor 350', 'Hunter 350', 'Himalayan',
      'Interceptor 650', 'Continental GT 650', 'Electra'
    ],
    'Mahindra': [
      'Centuro', 'Duro', 'Gusto', 'Mojo', 'Rodeo', 'Flyte', 'Pantero'
    ],
    'KTM': [
      'Duke 200', 'Duke 250', 'Duke 390', 'RC 200', 'RC 390'
    ],
    'Kawasaki': [
      'Ninja'
    ]
  };

  // ---------------------------------------------------------------------------
  // MODEL -> COLLECTION MAP (used by the "Browse all parts for X" smart redirect)
  // ---------------------------------------------------------------------------
  // When a user types just a bike model name (with no specific part word),
  // surface a special row at the top of the dropdown that links straight to
  // the model's collection page. Captures the 324K-impression "model-only"
  // search segment seen in our GSC analysis.
  //
  // Keys are lowercase-normalised. The detector strips common qualifier words
  // ('spare parts', 'price list', etc.) before lookup, so 'pulsar 150 spare
  // parts' and 'pulsar 150' both match the same entry.
  var MODEL_TO_COLLECTION = {
    // ---- YAMAHA ----
    'yamaha rx 100':          { url: '/collections/bike-yamaha-rx-100-spare-parts-list-at-best-price',                 display: 'Yamaha RX 100' },
    'rx 100':                 { url: '/collections/bike-yamaha-rx-100-spare-parts-list-at-best-price',                 display: 'Yamaha RX 100' },
    'rx100':                  { url: '/collections/bike-yamaha-rx-100-spare-parts-list-at-best-price',                 display: 'Yamaha RX 100' },
    'yamaha rx 135':          { url: '/collections/bike-yamaha-rx-135-spare-parts-list-at-best-price-1',               display: 'Yamaha RX 135' },
    'rx 135':                 { url: '/collections/bike-yamaha-rx-135-spare-parts-list-at-best-price-1',               display: 'Yamaha RX 135' },
    'rx135':                  { url: '/collections/bike-yamaha-rx-135-spare-parts-list-at-best-price-1',               display: 'Yamaha RX 135' },
    'yamaha fz':              { url: '/collections/bike-yamaha-fz-fzs-fz16-fz25-fazer-spare-parts-list-at-best-price', display: 'Yamaha FZ / FZS / FAZER' },
    'fz':                     { url: '/collections/bike-yamaha-fz-fzs-fz16-fz25-fazer-spare-parts-list-at-best-price', display: 'Yamaha FZ / FZS / FAZER' },
    'fzs':                    { url: '/collections/bike-yamaha-fz-fzs-fz16-fz25-fazer-spare-parts-list-at-best-price', display: 'Yamaha FZ / FZS / FAZER' },
    'fz v2':                  { url: '/collections/bike-yamaha-fz-fzs-fz16-fz25-fazer-spare-parts-list-at-best-price', display: 'Yamaha FZ V2' },
    'fz v3':                  { url: '/collections/bike-yamaha-fz-fzs-fz16-fz25-fazer-spare-parts-list-at-best-price', display: 'Yamaha FZ V3' },
    'fazer':                  { url: '/collections/bike-yamaha-fz-fzs-fz16-fz25-fazer-spare-parts-list-at-best-price', display: 'Yamaha Fazer' },
    'yamaha r15':             { url: '/collections/bike-yamaha-r15-r15s-v1-v2-v3-spare-parts-list-at-best-price',      display: 'Yamaha R15' },
    'r15':                    { url: '/collections/bike-yamaha-r15-r15s-v1-v2-v3-spare-parts-list-at-best-price',      display: 'Yamaha R15' },
    'r15 v2':                 { url: '/collections/bike-yamaha-r15-r15s-v1-v2-v3-spare-parts-list-at-best-price',      display: 'Yamaha R15 V2' },
    'r15 v3':                 { url: '/collections/bike-yamaha-r15-r15s-v1-v2-v3-spare-parts-list-at-best-price',      display: 'Yamaha R15 V3' },
    'yamaha fascino':         { url: '/collections/bike-yamaha-fascino-spare-parts-list-at-best-price',                display: 'Yamaha Fascino' },
    'fascino':                { url: '/collections/bike-yamaha-fascino-spare-parts-list-at-best-price',                display: 'Yamaha Fascino' },
    'yamaha crux':            { url: '/collections/bike-yamaha-crux-spare-parts-list-at-best-price',                   display: 'Yamaha Crux' },
    'crux':                   { url: '/collections/bike-yamaha-crux-spare-parts-list-at-best-price',                   display: 'Yamaha Crux' },
    'yamaha gladiator':       { url: '/collections/bike-yamaha-gladiator-spare-parts-list-at-best-price',              display: 'Yamaha Gladiator' },
    'gladiator':              { url: '/collections/bike-yamaha-gladiator-spare-parts-list-at-best-price',              display: 'Yamaha Gladiator' },
    'yamaha sz':              { url: '/collections/bike-yamaha-sz-spare-parts-list-at-best-price',                     display: 'Yamaha SZ' },
    'sz':                     { url: '/collections/bike-yamaha-sz-spare-parts-list-at-best-price',                     display: 'Yamaha SZ' },
    'szr':                    { url: '/collections/bike-yamaha-sz-spare-parts-list-at-best-price',                     display: 'Yamaha SZ-R' },
    'yamaha ray':             { url: '/collections/bike-yamaha-ray-spare-parts-list-at-best-price',                    display: 'Yamaha Ray' },
    'ray':                    { url: '/collections/bike-yamaha-ray-spare-parts-list-at-best-price',                    display: 'Yamaha Ray' },
    'ray zr':                 { url: '/collections/bike-yamaha-ray-spare-parts-list-at-best-price',                    display: 'Yamaha Ray ZR' },
    'yamaha libero':          { url: '/collections/bike-yamaha-libero-spare-parts-list-at-best-price',                 display: 'Yamaha Libero' },
    'libero':                 { url: '/collections/bike-yamaha-libero-spare-parts-list-at-best-price',                 display: 'Yamaha Libero' },

    // ---- HONDA ----
    'honda activa':           { url: '/collections/bike-honda-activa-3g-4g-5g-het-125-o-m-n-m-spare-parts-list-at-best-price', display: 'Honda Activa' },
    'activa':                 { url: '/collections/bike-honda-activa-3g-4g-5g-het-125-o-m-n-m-spare-parts-list-at-best-price', display: 'Honda Activa' },
    'activa 3g':              { url: '/collections/bike-honda-activa-3g-4g-5g-het-125-o-m-n-m-spare-parts-list-at-best-price', display: 'Honda Activa 3G' },
    'activa 4g':              { url: '/collections/bike-honda-activa-3g-4g-5g-het-125-o-m-n-m-spare-parts-list-at-best-price', display: 'Honda Activa 4G' },
    'activa 5g':              { url: '/collections/bike-honda-activa-3g-4g-5g-het-125-o-m-n-m-spare-parts-list-at-best-price', display: 'Honda Activa 5G' },
    'activa 6g':              { url: '/collections/bike-honda-activa-3g-4g-5g-het-125-o-m-n-m-spare-parts-list-at-best-price', display: 'Honda Activa 6G' },
    'activa 125':             { url: '/collections/bike-honda-activa-3g-4g-5g-het-125-o-m-n-m-spare-parts-list-at-best-price', display: 'Honda Activa 125' },
    'honda shine':            { url: '/collections/bike-honda-shine-spare-parts-list-at-best-price',                   display: 'Honda Shine' },
    'shine':                  { url: '/collections/bike-honda-shine-spare-parts-list-at-best-price',                   display: 'Honda Shine' },
    'cb shine':               { url: '/collections/bike-honda-shine-spare-parts-list-at-best-price',                   display: 'Honda CB Shine' },
    'shine sp':               { url: '/collections/bike-honda-shine-spare-parts-list-at-best-price',                   display: 'Honda Shine SP' },
    'sp 125':                 { url: '/collections/bike-honda-shine-spare-parts-list-at-best-price',                   display: 'Honda SP 125 / Shine' },
    'honda unicorn':          { url: '/collections/bike-honda-unicorn-spare-parts-list-at-best-price',                 display: 'Honda Unicorn' },
    'unicorn':                { url: '/collections/bike-honda-unicorn-spare-parts-list-at-best-price',                 display: 'Honda Unicorn' },
    'unicorn 150':            { url: '/collections/bike-honda-unicorn-spare-parts-list-at-best-price',                 display: 'Honda Unicorn 150' },
    'unicorn 160':            { url: '/collections/bike-honda-unicorn-spare-parts-list-at-best-price',                 display: 'Honda Unicorn 160' },
    'honda stunner':          { url: '/collections/bike-honda-stunner-spare-parts-list-at-best-price',                 display: 'Honda Stunner' },
    'stunner':                { url: '/collections/bike-honda-stunner-spare-parts-list-at-best-price',                 display: 'Honda Stunner' },
    'honda hornet':           { url: '/collections/honda-hornet',                                                      display: 'Honda Hornet' },
    'hornet':                 { url: '/collections/honda-hornet',                                                      display: 'Honda Hornet' },
    'hornet 160':             { url: '/collections/honda-hornet',                                                      display: 'Honda Hornet 160' },
    'honda dio':              { url: '/collections/honda-dio',                                                         display: 'Honda Dio' },
    'dio':                    { url: '/collections/honda-dio',                                                         display: 'Honda Dio' },
    'honda aviator':          { url: '/collections/honda-aviator',                                                     display: 'Honda Aviator' },
    'aviator':                { url: '/collections/honda-aviator',                                                     display: 'Honda Aviator' },
    'honda cb trigger':       { url: '/collections/honda-cb-trigger',                                                  display: 'Honda CB Trigger' },
    'cb trigger':             { url: '/collections/honda-cb-trigger',                                                  display: 'Honda CB Trigger' },
    'trigger':                { url: '/collections/honda-cb-trigger',                                                  display: 'Honda CB Trigger' },
    'honda cb twister':       { url: '/collections/honda-cb-twister',                                                  display: 'Honda CB Twister' },
    'cb twister':             { url: '/collections/honda-cb-twister',                                                  display: 'Honda CB Twister' },
    'twister':                { url: '/collections/honda-cb-twister',                                                  display: 'Honda CB Twister' },
    'honda cbr 150':          { url: '/collections/honda-cbr-150',                                                     display: 'Honda CBR 150' },
    'cbr 150':                { url: '/collections/honda-cbr-150',                                                     display: 'Honda CBR 150' },
    'cbr 150r':               { url: '/collections/honda-cbr-150',                                                     display: 'Honda CBR 150R' },
    'cbr150':                 { url: '/collections/honda-cbr-150',                                                     display: 'Honda CBR 150' },
    'honda cbr 250':          { url: '/collections/honda-cbr-250',                                                     display: 'Honda CBR 250' },
    'cbr 250':                { url: '/collections/honda-cbr-250',                                                     display: 'Honda CBR 250' },
    'cbr250':                 { url: '/collections/honda-cbr-250',                                                     display: 'Honda CBR 250' },
    'honda livo':             { url: '/collections/honda-livo',                                                        display: 'Honda Livo' },
    'livo':                   { url: '/collections/honda-livo',                                                        display: 'Honda Livo' },
    'honda grazia':           { url: '/collections/honda-grazia',                                                      display: 'Honda Grazia' },
    'grazia':                 { url: '/collections/honda-grazia',                                                      display: 'Honda Grazia' },
    'honda dream yuga':       { url: '/collections/honda-dream-yuga',                                                  display: 'Honda Dream Yuga' },
    'dream yuga':             { url: '/collections/honda-dream-yuga',                                                  display: 'Honda Dream Yuga' },
    'honda dream neo':        { url: '/collections/honda-dream-neo',                                                   display: 'Honda Dream Neo' },
    'dream neo':              { url: '/collections/honda-dream-neo',                                                   display: 'Honda Dream Neo' },
    'honda navi':             { url: '/collections/honda-navi',                                                        display: 'Honda Navi' },
    'navi':                   { url: '/collections/honda-navi',                                                        display: 'Honda Navi' },
    'honda cd 110':           { url: '/collections/honda-cd-110-dream-het',                                            display: 'Honda CD 110 Dream' },
    'cd 110 dream':           { url: '/collections/honda-cd-110-dream-het',                                            display: 'Honda CD 110 Dream' },

    // ---- HERO ----
    'hero splendor':          { url: '/collections/bike-hero-splendor-super-pro-plus-spare-parts-list-at-best-price',  display: 'Hero Splendor' },
    'splendor':               { url: '/collections/bike-hero-splendor-super-pro-plus-spare-parts-list-at-best-price',  display: 'Hero Splendor' },
    'splendor plus':          { url: '/collections/bike-hero-splendor-super-pro-plus-spare-parts-list-at-best-price',  display: 'Hero Splendor Plus' },
    'splendor pro':           { url: '/collections/bike-hero-splendor-super-pro-plus-spare-parts-list-at-best-price',  display: 'Hero Splendor Pro' },
    'super splendor':         { url: '/collections/bike-hero-splendor-super-pro-plus-spare-parts-list-at-best-price',  display: 'Hero Super Splendor' },
    'splendor ismart':        { url: '/collections/bike-hero-splendor-super-pro-plus-spare-parts-list-at-best-price',  display: 'Hero Splendor iSmart' },
    'hero passion':           { url: '/collections/bike-hero-passion-pro-x-pro-plus-spare-parts-list-at-best-price',   display: 'Hero Passion' },
    'passion':                { url: '/collections/bike-hero-passion-pro-x-pro-plus-spare-parts-list-at-best-price',   display: 'Hero Passion' },
    'passion pro':            { url: '/collections/bike-hero-passion-pro-x-pro-plus-spare-parts-list-at-best-price',   display: 'Hero Passion Pro' },
    'passion plus':           { url: '/collections/bike-hero-passion-pro-x-pro-plus-spare-parts-list-at-best-price',   display: 'Hero Passion Plus' },
    'passion x pro':          { url: '/collections/bike-hero-passion-pro-x-pro-plus-spare-parts-list-at-best-price',   display: 'Hero Passion X Pro' },
    'hero glamour':           { url: '/collections/bike-hero-glamour-spare-parts-list-at-best-price',                  display: 'Hero Glamour' },
    'glamour':                { url: '/collections/bike-hero-glamour-spare-parts-list-at-best-price',                  display: 'Hero Glamour' },
    'hf deluxe':              { url: '/collections/bike-hero-hf-deluxe-spare-parts-list-at-best-price',                display: 'Hero HF Deluxe' },
    'hero hf deluxe':         { url: '/collections/bike-hero-hf-deluxe-spare-parts-list-at-best-price',                display: 'Hero HF Deluxe' },
    'cbz xtreme':             { url: '/collections/bike-hero-cbz-xtreme-spare-parts-list-at-best-price',               display: 'Hero CBZ Xtreme' },
    'cbz':                    { url: '/collections/bike-hero-cbz-xtreme-spare-parts-list-at-best-price',               display: 'Hero CBZ Xtreme' },
    'hero hunk':              { url: '/collections/bike-hero-hunk-spare-parts-list-at-best-price',                     display: 'Hero Hunk' },
    'hunk':                   { url: '/collections/bike-hero-hunk-spare-parts-list-at-best-price',                     display: 'Hero Hunk' },
    'hero karizma':           { url: '/collections/bike-hero-karizma-spare-parts-list-at-best-price',                  display: 'Hero Karizma' },
    'karizma':                { url: '/collections/bike-hero-karizma-spare-parts-list-at-best-price',                  display: 'Hero Karizma' },
    'karizma zmr':            { url: '/collections/bike-hero-karizma-spare-parts-list-at-best-price',                  display: 'Hero Karizma ZMR' },
    'hero pleasure':          { url: '/collections/bike-hero-pleasure-spare-parts-list-at-best-price',                 display: 'Hero Pleasure' },
    'pleasure':               { url: '/collections/bike-hero-pleasure-spare-parts-list-at-best-price',                 display: 'Hero Pleasure' },
    'hero achiever':          { url: '/collections/bike-hero-achiever-spare-parts-list-at-best-price',                 display: 'Hero Achiever' },
    'achiever':               { url: '/collections/bike-hero-achiever-spare-parts-list-at-best-price',                 display: 'Hero Achiever' },
    'hero cd deluxe':         { url: '/collections/bike-hero-cd-deluxe-spare-parts-list-at-best-price',                display: 'Hero CD Deluxe' },
    'cd deluxe':              { url: '/collections/bike-hero-cd-deluxe-spare-parts-list-at-best-price',                display: 'Hero CD Deluxe' },
    'hero cd dawn':           { url: '/collections/hero-cd-dawn',                                                      display: 'Hero CD Dawn' },
    'cd dawn':                { url: '/collections/hero-cd-dawn',                                                      display: 'Hero CD Dawn' },
    'hero cd 100':            { url: '/collections/hero-cd-100',                                                       display: 'Hero CD 100' },
    'cd 100':                 { url: '/collections/hero-cd-100',                                                       display: 'Hero CD 100' },
    'hero maestro':           { url: '/collections/hero-maestro',                                                      display: 'Hero Maestro' },
    'maestro':                { url: '/collections/hero-maestro',                                                      display: 'Hero Maestro' },
    'maestro edge':           { url: '/collections/hero-maestro',                                                      display: 'Hero Maestro Edge' },
    'hero impulse':           { url: '/collections/hero-impulse',                                                      display: 'Hero Impulse' },
    'impulse':                { url: '/collections/hero-impulse',                                                      display: 'Hero Impulse' },
    'hero xpulse':            { url: '/collections/hero-xpulse',                                                       display: 'Hero Xpulse' },
    'xpulse':                 { url: '/collections/hero-xpulse',                                                       display: 'Hero Xpulse' },
    'hero xtreme':            { url: '/collections/hero-xtreme',                                                       display: 'Hero Xtreme' },
    'xtreme':                 { url: '/collections/hero-xtreme',                                                       display: 'Hero Xtreme' },
    'hero ambition':          { url: '/collections/hero-ambition',                                                     display: 'Hero Ambition' },
    'ambition':               { url: '/collections/hero-ambition',                                                     display: 'Hero Ambition' },
    'hero duet':              { url: '/collections/hero-duet',                                                         display: 'Hero Duet' },
    'duet':                   { url: '/collections/hero-duet',                                                         display: 'Hero Duet' },
    'hero ignitor':           { url: '/collections/hero-ignitor',                                                      display: 'Hero Ignitor' },
    'ignitor':                { url: '/collections/hero-ignitor',                                                      display: 'Hero Ignitor' },
    'hero hf dawn':           { url: '/collections/hero-hf-dawn',                                                      display: 'Hero HF Dawn' },
    'hf dawn':                { url: '/collections/hero-hf-dawn',                                                      display: 'Hero HF Dawn' },

    // ---- BAJAJ ----
    'pulsar 150':             { url: '/collections/bike-pulsar-150-spare-parts-list-at-best-price',                    display: 'Bajaj Pulsar 150' },
    'bajaj pulsar 150':       { url: '/collections/bike-pulsar-150-spare-parts-list-at-best-price',                    display: 'Bajaj Pulsar 150' },
    'pulsar 180':             { url: '/collections/bike-pulsar-180-spare-parts-list-at-best-price',                    display: 'Bajaj Pulsar 180' },
    'bajaj pulsar 180':       { url: '/collections/bike-pulsar-180-spare-parts-list-at-best-price',                    display: 'Bajaj Pulsar 180' },
    'pulsar 220':             { url: '/collections/bike-pulsar-220-spare-parts-list-at-best-price',                    display: 'Bajaj Pulsar 220' },
    'bajaj pulsar 220':       { url: '/collections/bike-pulsar-220-spare-parts-list-at-best-price',                    display: 'Bajaj Pulsar 220' },
    'platina':                { url: '/collections/platina-100-100-es-110-bs4-125',                                    display: 'Bajaj Platina' },
    'bajaj platina':          { url: '/collections/platina-100-100-es-110-bs4-125',                                    display: 'Bajaj Platina' },
    'platina 100':            { url: '/collections/platina-100-100-es-110-bs4-125',                                    display: 'Bajaj Platina 100' },
    'avenger 150':            { url: '/collections/bike-avenger-150-spare-parts-list-at-best-price',                   display: 'Bajaj Avenger 150 / 180 / 200' },
    'avenger':                { url: '/collections/bike-avenger-150-spare-parts-list-at-best-price',                   display: 'Bajaj Avenger' },
    'bajaj avenger':          { url: '/collections/bike-avenger-150-spare-parts-list-at-best-price',                   display: 'Bajaj Avenger' },
    'avenger 220':            { url: '/collections/bike-avenger-220-spare-parts-list-at-best-price',                   display: 'Bajaj Avenger 220' },
    'boxer':                  { url: '/collections/boxer-o-m-ct-ct-dlx-125-bm-100-bm-125',                             display: 'Bajaj Boxer' },
    'bajaj boxer':            { url: '/collections/boxer-o-m-ct-ct-dlx-125-bm-100-bm-125',                             display: 'Bajaj Boxer' },
    'ct 100':                 { url: '/collections/bike-ct-100-spare-parts-list-at-best-price',                        display: 'Bajaj CT 100' },
    'bajaj ct 100':           { url: '/collections/bike-ct-100-spare-parts-list-at-best-price',                        display: 'Bajaj CT 100' },
    'discover 100':           { url: '/collections/bike-discover-100-spare-parts-list-at-best-price',                  display: 'Bajaj Discover 100 / 110' },
    'discover 110':           { url: '/collections/bike-discover-100-spare-parts-list-at-best-price',                  display: 'Bajaj Discover 100 / 110' },
    'discover 125':           { url: '/collections/bike-discover-125-st-ss-t-m-new-model-old-model-bs4-spare-parts-list-at-best-price', display: 'Bajaj Discover 125' },
    'discover 125 st':        { url: '/collections/bike-discover-125-st-ss-t-m-new-model-old-model-bs4-spare-parts-list-at-best-price', display: 'Bajaj Discover 125 ST' },
    'discover 135':           { url: '/collections/bike-discover-135-spare-parts-list-at-best-price',                  display: 'Bajaj Discover 135' },
    'discover 150':           { url: '/collections/bike-discover-150-spare-parts-list-at-best-price',                  display: 'Bajaj Discover 150' },
    'xcd 125':                { url: '/collections/bike-xcd-125-spare-parts-list-at-best-price',                       display: 'Bajaj XCD 125' },
    'xcd 135':                { url: '/collections/bike-xcd-135-spare-parts-list-at-best-price',                       display: 'Bajaj XCD 135' },
    'vikrant':                { url: '/collections/bike-vikrant-v15-v12-spare-parts-list-at-best-price',               display: 'Bajaj Vikrant V15 / V12' },
    'vikrant v15':            { url: '/collections/bike-vikrant-v15-v12-spare-parts-list-at-best-price',               display: 'Bajaj Vikrant V15' },
    'v15':                    { url: '/collections/bike-vikrant-v15-v12-spare-parts-list-at-best-price',               display: 'Bajaj Vikrant V15' },

    // ---- TVS ----
    'apache rtr 160':         { url: '/collections/bike-tvs-apache-rtr-160-spare-parts-list-at-best-price',            display: 'TVS Apache RTR 160' },
    'tvs apache rtr 160':     { url: '/collections/bike-tvs-apache-rtr-160-spare-parts-list-at-best-price',            display: 'TVS Apache RTR 160' },
    'apache rtr 180':         { url: '/collections/bike-tvs-apache-rtr-180-spare-parts-list-at-best-price',            display: 'TVS Apache RTR 180' },
    'tvs apache rtr 180':     { url: '/collections/bike-tvs-apache-rtr-180-spare-parts-list-at-best-price',            display: 'TVS Apache RTR 180' },
    'apache rtr 200':         { url: '/collections/bike-tvs-apache-rtr-200-spare-parts-list-at-best-price',            display: 'TVS Apache RTR 200' },
    'tvs apache rtr 200':     { url: '/collections/bike-tvs-apache-rtr-200-spare-parts-list-at-best-price',            display: 'TVS Apache RTR 200' },
    'tvs xl':                 { url: '/collections/bike-tvs-xl-100-super-spare-parts-list-at-best-price',              display: 'TVS XL (100 / Super)' },
    'xl 100':                 { url: '/collections/bike-tvs-xl-100-super-spare-parts-list-at-best-price',              display: 'TVS XL 100' },
    'xl super':               { url: '/collections/bike-tvs-xl-100-super-spare-parts-list-at-best-price',              display: 'TVS XL Super' },
    'tvs xl super':           { url: '/collections/bike-tvs-xl-100-super-spare-parts-list-at-best-price',              display: 'TVS XL Super' },

    // ---- SUZUKI ----
    'access':                 { url: '/collections/bike-suzuki-access-spare-parts-list-at-best-price',                 display: 'Suzuki Access' },
    'suzuki access':          { url: '/collections/bike-suzuki-access-spare-parts-list-at-best-price',                 display: 'Suzuki Access' },
    'access 125':             { url: '/collections/bike-suzuki-access-spare-parts-list-at-best-price',                 display: 'Suzuki Access 125' },
    'gixxer':                 { url: '/collections/bike-suzuki-gixxer-spare-parts-list-at-best-price',                 display: 'Suzuki Gixxer' },
    'suzuki gixxer':          { url: '/collections/bike-suzuki-gixxer-spare-parts-list-at-best-price',                 display: 'Suzuki Gixxer' },
    'gixxer sf':              { url: '/collections/bike-suzuki-gixxer-spare-parts-list-at-best-price',                 display: 'Suzuki Gixxer SF' },
    'gixxer 155':             { url: '/collections/bike-suzuki-gixxer-spare-parts-list-at-best-price',                 display: 'Suzuki Gixxer 155' },
    'gs 150r':                { url: '/collections/bike-suzuki-gs-150r-spare-parts-list-at-best-price',                display: 'Suzuki GS 150R' },
    'suzuki gs 150r':         { url: '/collections/bike-suzuki-gs-150r-spare-parts-list-at-best-price',                display: 'Suzuki GS 150R' },
    'hayate':                 { url: '/collections/bike-suzuki-hayate-spare-parts-list-at-best-price',                 display: 'Suzuki Hayate' },
    'suzuki hayate':          { url: '/collections/bike-suzuki-hayate-spare-parts-list-at-best-price',                 display: 'Suzuki Hayate' },
    'intruder':               { url: '/collections/bike-suzuki-intruder-spare-parts-list-at-best-price',               display: 'Suzuki Intruder' },
    'lets':                   { url: '/collections/bike-suzuki-lets-spare-parts-list-at-best-price',                   display: 'Suzuki Lets' },
    'suzuki lets':            { url: '/collections/bike-suzuki-lets-spare-parts-list-at-best-price',                   display: 'Suzuki Lets' },
    'samurai':                { url: '/collections/bike-suzuki-samurai-spare-parts-list-at-best-price',                display: 'Suzuki Samurai' },
    'suzuki samurai':         { url: '/collections/bike-suzuki-samurai-spare-parts-list-at-best-price',                display: 'Suzuki Samurai' },
    'slingshot':              { url: '/collections/bike-suzuki-slingshot-spare-parts-list-at-best-price',              display: 'Suzuki Slingshot' },
    'swish':                  { url: '/collections/bike-suzuki-swish-spare-parts-list-at-best-price',                  display: 'Suzuki Swish' },
    'suzuki swish':           { url: '/collections/bike-suzuki-swish-spare-parts-list-at-best-price',                  display: 'Suzuki Swish' },
    'ax 100':                 { url: '/collections/suzuki-ax100-max-100',                                              display: 'Suzuki AX 100 / Max 100' },
    'ax100':                  { url: '/collections/suzuki-ax100-max-100',                                              display: 'Suzuki AX 100 / Max 100' },
    'max 100':                { url: '/collections/suzuki-ax100-max-100',                                              display: 'Suzuki Max 100' },
    'fiero':                  { url: '/collections/suzuki-fiero-fiero-f2',                                             display: 'Suzuki Fiero / Fiero F2' },

    // ---- MAHINDRA ----
    'duro':                   { url: '/collections/bike-mahindra-duro-spare-parts-list-at-best-price',                 display: 'Mahindra Duro' },
    'mahindra duro':          { url: '/collections/bike-mahindra-duro-spare-parts-list-at-best-price',                 display: 'Mahindra Duro' },
    'gusto':                  { url: '/collections/bike-mahindra-gusto-spare-parts-list-at-best-price',                display: 'Mahindra Gusto' },
    'mahindra gusto':         { url: '/collections/bike-mahindra-gusto-spare-parts-list-at-best-price',                display: 'Mahindra Gusto' },
    'rodeo':                  { url: '/collections/bike-mahindra-rodeo-spare-parts-list-at-best-price',                display: 'Mahindra Rodeo' },

    // ---- ROYAL ENFIELD & KAWASAKI (brand-level) ----
    'royal enfield':          { url: '/collections/online-spare-parts-price-list-for-royal-enfield-bikes',             display: 'Royal Enfield' },
    'bullet':                 { url: '/collections/online-spare-parts-price-list-for-royal-enfield-bikes',             display: 'Royal Enfield Bullet' },
    'bullet 350':             { url: '/collections/online-spare-parts-price-list-for-royal-enfield-bikes',             display: 'RE Bullet 350' },
    'classic 350':            { url: '/collections/online-spare-parts-price-list-for-royal-enfield-bikes',             display: 'RE Classic 350' },
    'thunderbird':            { url: '/collections/online-spare-parts-price-list-for-royal-enfield-bikes',             display: 'RE Thunderbird' },
    'meteor 350':             { url: '/collections/online-spare-parts-price-list-for-royal-enfield-bikes',             display: 'RE Meteor 350' },
    'kawasaki':               { url: '/collections/online-spare-parts-price-list-for-kawasaki-ninja-bikes',            display: 'Kawasaki Ninja' },
    'ninja':                  { url: '/collections/online-spare-parts-price-list-for-kawasaki-ninja-bikes',            display: 'Kawasaki Ninja' },

    // ---- Model->collection mappings for picker models that previously fell
    //      back to the brand collection (2026-06-30 fix). Handles verified via
    //      Admin API. Models with no dedicated collection (Raider, Chetak,
    //      Burgman, Avenis, Mojo, MT 15, RE 650s/Hunter/Himalayan) keep the
    //      brand collection, which is the closest available. ----
    'tvs apache 150': { url: '/collections/tvs-apache-150', display: 'TVS Apache 150' },
    'apache 150':     { url: '/collections/tvs-apache-150', display: 'TVS Apache 150' },
    'tvs jupiter':    { url: '/collections/tvs-jupiter', display: 'TVS Jupiter' },
    'jupiter':        { url: '/collections/tvs-jupiter', display: 'TVS Jupiter' },
    'tvs ntorq 125':  { url: '/collections/tvs-ntorq', display: 'TVS Ntorq' },
    'ntorq 125':      { url: '/collections/tvs-ntorq', display: 'TVS Ntorq' },
    'ntorq':          { url: '/collections/tvs-ntorq', display: 'TVS Ntorq' },
    'tvs star city':  { url: '/collections/tvs-star-city-100-110-plus', display: 'TVS Star City' },
    'star city':      { url: '/collections/tvs-star-city-100-110-plus', display: 'TVS Star City' },
    'tvs radeon':     { url: '/collections/tvs-radeon', display: 'TVS Radeon' },
    'radeon':         { url: '/collections/tvs-radeon', display: 'TVS Radeon' },
    'tvs phoenix':    { url: '/collections/tvs-phoenix', display: 'TVS Phoenix' },
    'phoenix':        { url: '/collections/tvs-phoenix', display: 'TVS Phoenix' },
    'tvs victor':     { url: '/collections/tvs-victor-gl-glx-n-m-o-m', display: 'TVS Victor' },
    'victor':         { url: '/collections/tvs-victor-gl-glx-n-m-o-m', display: 'TVS Victor' },
    'tvs sport':      { url: '/collections/tvs-star-sport', display: 'TVS Star Sport' },
    'tvs flame':      { url: '/collections/tvs-flame', display: 'TVS Flame' },
    'flame':          { url: '/collections/tvs-flame', display: 'TVS Flame' },
    'tvs wego':       { url: '/collections/tvs-wego', display: 'TVS Wego' },
    'wego':           { url: '/collections/tvs-wego', display: 'TVS Wego' },
    'tvs scooty pep': { url: '/collections/tvs-scooty-pep-pep-plus', display: 'TVS Scooty Pep' },
    'scooty pep':     { url: '/collections/tvs-scooty-pep-pep-plus', display: 'TVS Scooty Pep' },
    'tvs centra':     { url: '/collections/tvs-centra', display: 'TVS Centra' },
    'centra':         { url: '/collections/tvs-centra', display: 'TVS Centra' },
    'tvs streak':     { url: '/collections/tvs-streak', display: 'TVS Streak' },
    'streak':         { url: '/collections/tvs-streak', display: 'TVS Streak' },
    'tvs zest':       { url: '/collections/tvs-zest', display: 'TVS Zest' },
    'zest':           { url: '/collections/tvs-zest', display: 'TVS Zest' },
    'tvs jive':       { url: '/collections/tvs-jive', display: 'TVS Jive' },
    'jive':           { url: '/collections/tvs-jive', display: 'TVS Jive' },
    'tvs max 4r':     { url: '/collections/tvs-max-4r', display: 'TVS Max 4R' },
    'max 4r':         { url: '/collections/tvs-max-4r', display: 'TVS Max 4R' },
    'bajaj pulsar 125': { url: '/collections/pulsar-135', display: 'Bajaj Pulsar 125 / 135' },
    'pulsar 125':       { url: '/collections/pulsar-135', display: 'Bajaj Pulsar 125 / 135' },
    'bajaj pulsar 135': { url: '/collections/pulsar-135', display: 'Bajaj Pulsar 125 / 135' },
    'pulsar 135':       { url: '/collections/pulsar-135', display: 'Bajaj Pulsar 125 / 135' },
    'bajaj pulsar 160': { url: '/collections/pulsar-160-as-ns-ns-bs4-ns-fi', display: 'Bajaj Pulsar 160' },
    'pulsar 160':       { url: '/collections/pulsar-160-as-ns-ns-bs4-ns-fi', display: 'Bajaj Pulsar 160' },
    'bajaj pulsar ns 200': { url: '/collections/pulsar-200-ug3-ug4-ug4-5-as-ns-rs-ns-bs4-rs-bs4', display: 'Bajaj Pulsar 200' },
    'pulsar ns 200':       { url: '/collections/pulsar-200-ug3-ug4-ug4-5-as-ns-rs-ns-bs4-rs-bs4', display: 'Bajaj Pulsar 200' },
    'bajaj caliber':    { url: '/collections/caliber', display: 'Bajaj Caliber' },
    'caliber':          { url: '/collections/caliber', display: 'Bajaj Caliber' },
    'bajaj dominar 400':{ url: '/collections/dominar-400', display: 'Bajaj Dominar 400' },
    'dominar 400':      { url: '/collections/dominar-400', display: 'Bajaj Dominar 400' },
    'honda eterno':   { url: '/collections/honda-eterno', display: 'Honda Eterno' },
    'eterno':         { url: '/collections/honda-eterno', display: 'Honda Eterno' },
    'yamaha fz-s':    { url: '/collections/bike-yamaha-fz-fzs-fz16-fz25-fazer-spare-parts-list-at-best-price', display: 'Yamaha FZ-S' },
    'fz-s':           { url: '/collections/bike-yamaha-fz-fzs-fz16-fz25-fazer-spare-parts-list-at-best-price', display: 'Yamaha FZ-S' },
    'yamaha saluto':  { url: '/collections/yamaha-saluto', display: 'Yamaha Saluto' },
    'saluto':         { url: '/collections/yamaha-saluto', display: 'Yamaha Saluto' },
    'yamaha alba':    { url: '/collections/yamaha-alba', display: 'Yamaha Alba' },
    'alba':           { url: '/collections/yamaha-alba', display: 'Yamaha Alba' },
    'yamaha enticer': { url: '/collections/yamaha-enticer', display: 'Yamaha Enticer' },
    'enticer':        { url: '/collections/yamaha-enticer', display: 'Yamaha Enticer' },
    'yamaha ybr':     { url: '/collections/yamaha-ybr-100-110-125', display: 'Yamaha YBR' },
    'ybr':            { url: '/collections/yamaha-ybr-100-110-125', display: 'Yamaha YBR' },
    'yamaha ybx':     { url: '/collections/yamaha-ybx', display: 'Yamaha YBX' },
    'ybx':            { url: '/collections/yamaha-ybx', display: 'Yamaha YBX' },
    'yamaha sz-r':    { url: '/collections/bike-yamaha-sz-spare-parts-list-at-best-price', display: 'Yamaha SZ' },
    'sz-r':           { url: '/collections/bike-yamaha-sz-spare-parts-list-at-best-price', display: 'Yamaha SZ' },
    'yamaha sz-rr':   { url: '/collections/bike-yamaha-sz-spare-parts-list-at-best-price', display: 'Yamaha SZ' },
    'sz-rr':          { url: '/collections/bike-yamaha-sz-spare-parts-list-at-best-price', display: 'Yamaha SZ' },
    'yamaha alpha':   { url: '/collections/yamaha-alpha', display: 'Yamaha Alpha' },
    'alpha':          { url: '/collections/yamaha-alpha', display: 'Yamaha Alpha' },
    'suzuki zeus':    { url: '/collections/suzuki-zeus', display: 'Suzuki Zeus' },
    'zeus':           { url: '/collections/suzuki-zeus', display: 'Suzuki Zeus' },
    'mahindra centuro':{ url: '/collections/mahindra-centuro', display: 'Mahindra Centuro' },
    'centuro':         { url: '/collections/mahindra-centuro', display: 'Mahindra Centuro' },
    'mahindra flyte': { url: '/collections/mahindra-flyte', display: 'Mahindra Flyte' },
    'flyte':          { url: '/collections/mahindra-flyte', display: 'Mahindra Flyte' },
    'mahindra pantero':{ url: '/collections/mahindra-pantero', display: 'Mahindra Pantero' },
    'pantero':         { url: '/collections/mahindra-pantero', display: 'Mahindra Pantero' },
    'ktm':            { url: '/collections/ktm-products', display: 'KTM' },

    // ---- BRAND-LEVEL fallbacks (when user just types a brand) ----
    'bajaj':                  { url: '/collections/online-bajaj-bike-spare-parts-at-best-price',                       display: 'All Bajaj bikes' },
    'hero':                   { url: '/collections/online-hero-bike-spare-parts-at-best-price',                        display: 'All Hero bikes' },
    'honda':                  { url: '/collections/online-honda-bike-spare-parts-at-best-price',                       display: 'All Honda bikes' },
    'yamaha':                 { url: '/collections/online-yamaha-bike-spare-parts-at-best-price',                      display: 'All Yamaha bikes' },
    'tvs':                    { url: '/collections/online-tvs-bike-spare-parts-at-best-price',                         display: 'All TVS bikes' },
    'suzuki':                 { url: '/collections/online-suzuki-bike-spare-parts-at-best-price',                      display: 'All Suzuki bikes' },
    'mahindra':               { url: '/collections/online-mahindra-bike-spare-parts-at-best-price',                    display: 'All Mahindra bikes' }
  };

  // Common qualifier words to strip before lookup. After stripping, what remains
  // should be just the model name. These come straight from the GSC top-200 queries.
  var MODEL_LOOKUP_NOISE = /\b(spare\s*parts?|bike\s*parts?|bike\s*spare|spare|parts?|price\s*list|price|online\s*shopping|online|shopping|original|genuine|all|cc|new\s*model|old\s*model)\b/g;

  // Words that, if present in the query, mean the user is searching for a SPECIFIC
  // part - so we DON'T want to redirect them to a generic collection page. They
  // want a product, not a category.
  var PART_INDICATOR_WORDS = /\b(brake|brak|break|carb|carburet|silencer|cilencer|tyre|tire|tayar|battery|seat|tank|tenk|tanki|headlight|head\s*light|hade\s*light|tail|teal|indicator|blinker|shock|shoker|shokar|mirror|mudguard|fender|fork|spark|plug|chain|sprocket|spocet|sproket|piston|cylinder|engine|clutch|cluch|cable|wire|wiring|waring|harness|haness|meter|metre|mettar|mitar|speedometer|cdi|ecu|magneto|stator|coil|switch|fuel|petrol|exhaust|panel|cover|body|guard|filter|stand|footrest|grip|bracket|lock|key|kit|rim|wheel|bulb|lamp|light|horn|hooter|rectifier|ignition|starter|motor|throttle|carby|carburettor|magneto|sticker|visor|gear|bearing|crank|piston|gasket)\b/i;

  function findCollectionForModel(typed) {
    if (!typed || typeof typed !== 'string') return null;
    var raw = typed.toLowerCase().trim();
    if (!raw) return null;
    // If the query mentions a specific part, the user wants products - bail.
    if (PART_INDICATOR_WORDS.test(raw)) return null;
    // Strip noise words and collapse whitespace.
    var normalized = raw.replace(MODEL_LOOKUP_NOISE, ' ').replace(/\s+/g, ' ').trim();
    if (!normalized) return null;
    if (MODEL_TO_COLLECTION[normalized]) return MODEL_TO_COLLECTION[normalized];
    // Try without the original spacing too (e.g. 'rx100' vs 'rx 100')
    var compact = normalized.replace(/\s+/g, '');
    if (MODEL_TO_COLLECTION[compact]) return MODEL_TO_COLLECTION[compact];
    return null;
  }

  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  var state = {
    activeVehicle: null,   // { brand: 'Honda', model: 'Activa 6G' } | null
    debounceId: null,
    abortCtrl: null,
    activeIndex: -1,
    currentResults: [],
    isOpen: false
  };

  // --- search-analytics hooks (no-op if the analytics asset isn't loaded) ---
  var _saLastQuery = '';
  function emitSA(name, detail) {
    try { document.dispatchEvent(new CustomEvent(name, { detail: detail })); } catch (e) {}
  }
  function saVehicle() {
    var v = state.activeVehicle || {};
    return { vehicle_brand: v.brand || null, vehicle_model: v.model || null };
  }

  // ---------------------------------------------------------------------------
  // UTILITIES
  // ---------------------------------------------------------------------------
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function debounce(fn, ms) {
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(state.debounceId);
      state.debounceId = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    // Shopify returns prices as strings like "1234.00" (INR has no sub-units in display).
    // We render with &#8377; + Indian comma grouping.
    if (cents == null || cents === '') return '';
    var num = typeof cents === 'string' ? parseFloat(cents) : cents;
    if (isNaN(num)) return '';
    // Indian numbering: 1,23,456 not 123,456
    var rounded = Math.round(num);
    var s = String(rounded);
    var lastThree = s.substring(s.length - 3);
    var other = s.substring(0, s.length - 3);
    if (other) lastThree = ',' + lastThree;
    other = other.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    return '&#8377;' + other + lastThree;
  }

  function highlightMatch(text, query) {
    if (!query) return escapeHtml(text);
    var safeText = escapeHtml(text);
    var tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    tokens.forEach(function (tok) {
      if (tok.length < 2) return;
      var rx = new RegExp('(' + tok.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') + ')', 'ig');
      safeText = safeText.replace(rx, '<mark>$1</mark>');
    });
    return safeText;
  }

  function safeStorage(action, key, val) {
    try {
      if (action === 'get') return localStorage.getItem(key);
      if (action === 'set') return localStorage.setItem(key, val);
      if (action === 'del') return localStorage.removeItem(key);
    } catch (e) { /* private mode / disabled */ }
    return null;
  }

  // --- Recent searches (localStorage, capped at CONFIG.maxRecent) ------------
  // Stored as a JSON array of plain query strings, most-recent-first.
  function getRecents() {
    var raw = safeStorage('get', CONFIG.recentKey);
    if (!raw) return [];
    try {
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(function (x) { return typeof x === 'string'; }) : [];
    } catch (e) { return []; }
  }

  function pushRecent(term) {
    var t = (term || '').trim();
    if (!t || t.length < CONFIG.minChars) return;
    var lower = t.toLowerCase();
    var list = getRecents().filter(function (x) { return x.toLowerCase() !== lower; });
    list.unshift(t);
    list = list.slice(0, CONFIG.maxRecent);
    safeStorage('set', CONFIG.recentKey, JSON.stringify(list));
  }

  function clearRecents() {
    safeStorage('del', CONFIG.recentKey);
  }

  // ---------------------------------------------------------------------------
  // QUERY NORMALIZATION - regex rules that fold predictable spelling variants
  // ---------------------------------------------------------------------------
  //
  // Pattern-based normalization complements the SYNONYMS dictionary. The dict
  // handles UNPREDICTABLE Hinglish phonetics (sokar/shokar/shoker/soka - no
  // pattern, just memorise). Regex handles PREDICTABLE variations that would
  // otherwise need 80+ dictionary entries:
  //   - number/letter spacing variants  ("mt 15", "mt-15", "mt15" all same)
  //   - filler words                    ("spare parts", "price list", "online")
  //   - engine displacement suffix      ("150 cc carburetor" = "150 carburetor")
  //
  // Strategy: we produce a normalised form and add it as an extra expansion
  // ALONGSIDE the raw query (not as a replacement). Recall is preserved - if
  // a product is titled with the user's exact form, the raw query still finds
  // it; the normalised form just rescues misses caused by spacing/noise.
  //
  // Examples:
  //   "mt 15"                  -> "mt15"
  //   "r 15 v3"                -> "r15v3"
  //   "cd-100"                 -> "cd100"
  //   "pulsar 150 spare parts" -> "pulsar150"
  //   "150 cc carburetor"      -> "150 carburetor"
  //   "brake"                  -> "brake"   (no-op - nothing to normalise)

  // Filler words conservatively chosen: only words that genuinely add no
  // search-relevance signal. Excluded "for/to/at/the/in/of" because they can
  // appear in real product queries ("foot peg for pulsar" - meaningful).
  var FILLER_WORDS = /\b(spare\s*parts?|spares|spare|parts?|part|price\s*list|price|list|online\s*shopping|online|shopping|original|genuine|oem|new\s*model|old\s*model|buy)\b/gi;

  function normalizeQuery(query) {
    if (!query || typeof query !== 'string') return '';
    var q = query.toLowerCase().trim();
    if (!q) return '';
    // Order matters! Strip fillers FIRST while word boundaries still exist.
    // In JS regex, digits and letters are both \w, so a digit-letter
    // transition is NOT a word boundary - if we collapsed "150 spare" ->
    // "150spare" first, then \bspare\b couldn't match inside "150spare".

    // 1. Strip filler words.
    q = q.replace(FILLER_WORDS, ' ');
    // 2. Strip "cc" displacement suffix when it follows digits. "150 cc" -> "150"
    q = q.replace(/(\d+)\s*cc\b/gi, '$1');
    // 3. Collapse spaces/hyphens/underscores between letters and digits.
    //    "mt 15" -> "mt15", "r 15 v3" -> "r15v3", "cd-100" -> "cd100"
    //
    //    Letter->digit (e.g. "mt 15") is generally safe because parts queries
    //    rarely put a number after a non-model word.
    //
    //    Digit->letter is RESTRICTED to short letter runs (1-3 chars) so we
    //    only collapse model-variant suffixes (v1, v2, v3, r, s, z, etc.) and
    //    don't accidentally glue "150 carburetor" into "150carburetor".
    q = q.replace(/([a-z])[\s\-_]+(\d)/gi, '$1$2');
    // Negative lookahead `(?![a-z])` (not `\b`) because `\b` doesn't fire at
    // letter->digit transitions in JS - both are \w. We need to allow letters
    // followed by digits (model-suffix "v3") to be collapsed too.
    q = q.replace(/(\d)[\s\-_]+([a-z]{1,3})(?![a-z])/gi, '$1$2');
    // 4. Collapse multi-whitespace.
    q = q.replace(/\s+/g, ' ').trim();
    return q;
  }

  // For "See all results" navigation and form submit. The dropdown finds
  // products via our regex+synonym expansion through /search/suggest.json,
  // but the /search results page uses Shopify's server-side search which
  // (a) doesn't know about our normalisation and (b) wants SPACED tokens -
  // it tokenises by whitespace, so "r15 v3" (two tokens) finds R15 V3
  // products but "r15v3" (one token) doesn't.
  //
  // So this helper does:
  //   1. Strip filler words ("spare parts", "price list", ...) so they don't
  //      dilute the AND-match against product titles
  //   2. Apply synonym rescue ("palsa" -> "pulsar", "r15v3" -> "r15 v3")
  //   3. DOES NOT collapse spacing - server search wants spaces preserved
  //
  // Examples:
  //   "r15v3"                  -> "r15 v3"
  //   "palsa"                  -> "pulsar"
  //   "apch"                   -> "apache"
  //   "pulsar 150"             -> "pulsar 150"  (no change - already optimal)
  //   "pulsar 150 spare parts" -> "pulsar 150"  (fillers stripped)
  function bestSearchUrlQuery(userQuery) {
    if (!userQuery || typeof userQuery !== 'string') return userQuery || '';
    var q = userQuery.toLowerCase().trim();
    if (!q) return userQuery;
    // 1. Strip filler words.
    q = q.replace(FILLER_WORDS, ' ').replace(/\s+/g, ' ').trim();
    if (!q) return userQuery;
    // 2. Apply first matching synonym (one-shot, no fan-out - we want a
    //    deterministic single URL).
    var keys = Object.keys(SYNONYMS);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      var rx = new RegExp(
        '\\b' + key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') + '\\b',
        'i'
      );
      if (rx.test(q)) {
        q = q.replace(rx, SYNONYMS[key][0]);
        break; // first match wins for URL purposes
      }
    }
    return q.trim() || userQuery;
  }

  // Build the full /search URL - best-expanded query AND vehicle-tag prefix
  // if a vehicle is currently selected. Used by both the "See all results"
  // link and the suggestion-click handler so the user keeps their vehicle
  // context when leaving the dropdown.
  function buildSearchUrl(userQuery) {
    var rd = redirectFor(userQuery);
    if (rd) return rd; // dashboard-managed redirect wins (e.g. "tyres" -> /collections/tyres)
    var q = bestSearchUrlQuery(userQuery);
    // TEXT-based vehicle prefix (not tag: filter) - matches v9 behavior and
    // avoids the result-count regression where strict tag AND excludes
    // products that mention the bike in title but aren't VehicleModel_X tagged.
    if (state.activeVehicle) {
      var prefix = '';
      if (state.activeVehicle.brand) prefix += state.activeVehicle.brand + ' ';
      if (state.activeVehicle.model) prefix += state.activeVehicle.model + ' ';
      q = (prefix + q).trim();
    }
    return CONFIG.searchPageUrl + '?q=' + encodeURIComponent(q) + '&type=product';
  }

  // ---------------------------------------------------------------------------
  // REMOTE CONFIG - dashboard-managed synonyms / trending / vehicles / redirects
  // Stale-while-revalidate: apply cached config instantly, then refresh from the
  // app proxy in the background. Always falls back to the built-in defaults, so
  // the search keeps working even if the analytics app is unreachable.
  // ---------------------------------------------------------------------------
  var REDIRECTS = {};
  var CONFIG_CACHE_KEY = 'eauto.search.cfg.v1';
  function redirectFor(q) {
    var r = REDIRECTS[(q || '').toLowerCase().trim()];
    return (r && r.url) ? r.url : null;
  }
  function applyRemoteConfig(cfg) {
    if (!cfg) return false;
    var addedVehicle = false;
    if (cfg.synonyms) {
      Object.keys(cfg.synonyms).forEach(function (k) {
        var v = cfg.synonyms[k];
        if (Array.isArray(v) && v.length) SYNONYMS[String(k).toLowerCase()] = v;
      });
    }
    if (Array.isArray(cfg.trending) && cfg.trending.length) {
      TRENDING_SEARCHES = cfg.trending.slice(0, 8);
    }
    if (cfg.vehicles) {
      Object.keys(cfg.vehicles).forEach(function (b) {
        if (!VEHICLES[b]) VEHICLES[b] = [];
        (cfg.vehicles[b] || []).forEach(function (m) {
          if (VEHICLES[b].indexOf(m) === -1) { VEHICLES[b].push(m); addedVehicle = true; }
        });
      });
    }
    if (cfg.redirects && typeof cfg.redirects === 'object') REDIRECTS = cfg.redirects;
    return addedVehicle;
  }
  function loadRemoteConfig() {
    try {
      var c = safeStorage('get', CONFIG_CACHE_KEY);
      if (c) applyRemoteConfig(JSON.parse(c));
    } catch (e) {}
    fetch('/apps/search-analytics/config', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cfg) {
        if (!cfg) return;
        try { safeStorage('set', CONFIG_CACHE_KEY, JSON.stringify(cfg)); } catch (e) {}
        if (applyRemoteConfig(cfg)) { try { renderVehicleFilter(); } catch (e) {} }
      })
      .catch(function () {});
  }

  // ---------------------------------------------------------------------------
  // QUERY BUILDING - synonyms + Hinglish + vehicle tags
  // ---------------------------------------------------------------------------
  function expandSynonyms(query) {
    var raw = query.toLowerCase().trim();
    if (!raw) return [];
    var expansions = [raw];
    // Add the regex-normalised form as a second variant if it's different
    // (and non-empty - e.g. a pure-noise query like "online shopping" stays
    // as the raw form rather than collapsing to nothing).
    var norm = normalizeQuery(query);
    if (norm && norm !== raw && expansions.indexOf(norm) === -1) {
      expansions.push(norm);
    }
    // Synonym expansion runs against BOTH raw and normalised forms so dict
    // entries keyed on either spelling variant fire correctly.
    var variants = norm && norm !== raw ? [raw, norm] : [raw];
    Object.keys(SYNONYMS).forEach(function (key) {
      // Word-boundary match - prevents short keys like 're', 'rim', 'meter'
      // from matching inside unrelated words ('tyre', 'trim', 'speedometer').
      var rx = new RegExp(
        '\\b' + key.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') + '\\b',
        'i'
      );
      variants.forEach(function (variant) {
        if (!rx.test(variant)) return;
        SYNONYMS[key].forEach(function (syn) {
          var swapped = variant.replace(rx, syn);
          if (expansions.indexOf(swapped) === -1) expansions.push(swapped);
        });
      });
    });
    return expansions.slice(0, 4); // cap fan-out - raw + normalised + 2 synonyms
  }

  function buildSearchQuery(userText) {
    // If a vehicle is selected, prepend the bike's brand+model as TEXT (not
    // as tag: filters). Why text? Strict `tag:"VehicleModel_X"` AND filtering
    // misses products that MENTION the bike in their title but aren't
    // explicitly tagged. The product-tagging audit is improving this but
    // until tagging is comprehensive, text-based match matches v9 behavior
    // and avoids result-count regressions. See T2.5 in Asana project.
    var prefix = '';
    if (state.activeVehicle) {
      if (state.activeVehicle.brand) prefix += state.activeVehicle.brand + ' ';
      if (state.activeVehicle.model) prefix += state.activeVehicle.model + ' ';
    }
    return (prefix + userText).trim();
  }

  // ---------------------------------------------------------------------------
  // NETWORK - Shopify Predictive Search API
  // ---------------------------------------------------------------------------
  function predictiveSearch(query, signal) {
    // NOTE: We hand-build the query string because URLSearchParams percent-
    // encodes square brackets (resources%5Btype%5D), and Shopify's predictive
    // search endpoint only honours the literal `resources[...]` keys.
    //
    // `fields` is explicitly listed because Shopify's default ONLY searches
    // title + tag for products - meaning OEM part-number queries like
    // "JL40020" or "5860" return nothing despite the number sitting in the
    // variant SKU. Adding variants.sku + variants.barcode makes those
    // high-intent mechanic/dealer searches actually find products.
    var qs = 'q=' + encodeURIComponent(query)
      + '&resources[type]=product,query'
      + '&resources[limit]=' + CONFIG.maxResults
      + '&resources[options][unavailable_products]=last'
      + '&resources[options][fields]=title,vendor,product_type,variants.title,variants.sku,variants.barcode,tag';
    return fetch(CONFIG.apiUrl + '?' + qs, {
      signal: signal,
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('search ' + r.status);
      return r.json();
    });
  }

  // Request-token guard: every call increments the token; resolvers bail
  // if a newer call has started since they fired.
  runMultiQuery._token = 0;
  function runMultiQuery(userText) {
    if (state.abortCtrl) state.abortCtrl.abort();
    state.abortCtrl = ('AbortController' in window) ? new AbortController() : null;
    var signal = state.abortCtrl ? state.abortCtrl.signal : undefined;
    var token = (++runMultiQuery._token);

    var expansions = expandSynonyms(userText);
    var queries = expansions.map(function (e) { return buildSearchQuery(e); });
    var calls = queries.map(function (q) {
      return predictiveSearch(q, signal).catch(function (err) {
        if (err && err.name === 'AbortError') throw err; // propagate aborts
        return null;
      });
    });

    // Helper - uniform "stale" sentinel returned from any path where the
    // request token has been bumped by a newer keystroke.
    function staleResult() {
      return { products: [], suggestions: [], fallbackUsedNoVehicle: false, _stale: true };
    }

    return Promise.all(calls).then(function (responses) {
      if (token !== runMultiQuery._token) return staleResult();
      var merged = mergeResponses(responses);

      // Happy path - we have results.
      if (merged.products.length > 0) return merged;

      // No results - escalate through fallbacks in order. Each fallback runs
      // only if the previous one still returned zero. Every async boundary
      // re-checks the token so a stale fallback cannot stomp a newer query.

      var p = Promise.resolve(merged);

      // FALLBACK 1: Drop the vehicle tags and retry the original query.
      // Use normalised query - picks up the same spacing/filler-strip wins
      // that the primary path got.
      if (state.activeVehicle) {
        p = p.then(function (current) {
          if (token !== runMultiQuery._token) return staleResult();
          var fb1q = normalizeQuery(userText) || userText;
          return predictiveSearch(fb1q, signal).then(function (raw) {
            if (token !== runMultiQuery._token) return staleResult();
            var r = raw && raw.resources && raw.resources.results;
            if (!r) return current;
            return {
              products: (r.products || []).slice(0, CONFIG.maxResults),
              suggestions: (r.queries || []).slice(0, CONFIG.maxSuggestions),
              fallbackUsedNoVehicle: true
            };
          }).catch(function (err) {
            if (err && err.name === 'AbortError') throw err;
            if (token !== runMultiQuery._token) return staleResult();
            return current;
          });
        });
      }

      // FALLBACK 2: Typo correction - if Shopify's suggestion API returned a
      // term that differs from the user input, retry with that term. Catches
      // "brke" -> "brake", "corporetor" -> "carburetor", etc.
      p = p.then(function (current) {
        if (current && current._stale) return current;
        if (token !== runMultiQuery._token) return staleResult();
        if (current.products.length > 0) return current;
        if (!current.suggestions || !current.suggestions.length) return current;
        var raw = current.suggestions[0].text;
        if (!raw) return current;
        var lc = raw.toLowerCase().trim();
        var orig = userText.toLowerCase().trim();
        if (lc === orig) return current; // identical, skip
        // Skip *only* single-word prefix extensions ("act" -> "actuator")
        // because those are mid-typing autocomplete, not corrections. Multi-
        // word suggestions like "brak" -> "brake pad" are still useful retries.
        if (lc.split(/\s+/).length === 1 && orig.split(/\s+/).length === 1
            && lc.indexOf(orig) === 0) return current;
        return predictiveSearch(raw, signal).then(function (resp) {
          if (token !== runMultiQuery._token) return staleResult();
          var r = resp && resp.resources && resp.resources.results;
          if (!r || !r.products || !r.products.length) return current;
          return {
            products: r.products.slice(0, CONFIG.maxResults),
            suggestions: current.suggestions,
            fallbackUsedNoVehicle: current.fallbackUsedNoVehicle,
            correctedFrom: userText,
            correctedTo: raw
          };
        }).catch(function (err) {
          if (err && err.name === 'AbortError') throw err;
          if (token !== runMultiQuery._token) return staleResult();
          return current;
        });
      });

      return p;
    });
  }

  function mergeResponses(responses) {
    var seen = Object.create(null);
    var products = [];
    var suggestions = [];
    var seenSugg = Object.create(null);
    responses.forEach(function (resp) {
      if (!resp || !resp.resources || !resp.resources.results) return;
      var r = resp.resources.results;
      (r.products || []).forEach(function (p) {
        if (!seen[p.id]) {
          seen[p.id] = true;
          products.push(p);
        }
      });
      (r.queries || []).forEach(function (q) {
        var key = (q.text || '').toLowerCase();
        if (key && !seenSugg[key]) {
          seenSugg[key] = true;
          suggestions.push(q);
        }
      });
    });
    return {
      products: products.slice(0, CONFIG.maxResults),
      suggestions: suggestions.slice(0, CONFIG.maxSuggestions),
      fallbackUsedNoVehicle: false
    };
  }

  // ---------------------------------------------------------------------------
  // RENDERING
  // ---------------------------------------------------------------------------
  function showSkeleton(inner) {
    inner.innerHTML = ''
      + '<div class="eauto-pred__loading" role="status" aria-live="polite">'
      + '<span class="eauto-pred__spinner" aria-hidden="true"></span>'
      + '<span class="visually-hidden">Searching...</span>'
      + '</div>';
  }

  // Resolves the currently-selected vehicle to a collection URL. Tries
  // "<brand> <model>" then "<model>" then "<brand>" as keys in
  // MODEL_TO_COLLECTION. Falls back to /search?q=tag:"..." if no
  // explicit collection mapping exists.
  function getVehicleCollectionUrl() {
    if (!state.activeVehicle) return CONFIG.searchPageUrl;
    // Product decision (2026-06-30): "Browse all parts for <bike>" goes to the
    // TEXT SEARCH results, not the curated collection page. Text search returns
    // every product that names the bike in its title - more comprehensive than
    // the collection, which depends on complete VehicleModel_ tagging (and that
    // tagging drifts). The MODEL_TO_COLLECTION map is kept only for detecting /
    // labelling model-only queries, not for navigation.
    var v = state.activeVehicle;
    var prefix = '';
    if (v.brand) prefix += v.brand + ' ';
    if (v.model) prefix += v.model + ' ';
    return CONFIG.searchPageUrl + '?q=' + encodeURIComponent(prefix.trim()) + '&type=product';
  }

  // ---------------------------------------------------------------------------
  // EMPTY-STATE PANEL - shown when search dropdown is open with no query.
  // Replaces the "broken blue void" UX with: a prominent vehicle-pick CTA
  // (or current-vehicle indicator) + browse-by-category chips. Especially
  // important on mobile (91% of traffic) where the search overlay is full-screen.
  // ---------------------------------------------------------------------------
  function renderEmptyState() {
    var inner = $(CONFIG.selectors.resultsInner);
    if (!inner) return;
    var v = state.activeVehicle;
    var hasVehicle = !!(v && v.brand);

    // Empty-state CTA - when vehicle is selected, the PRIMARY action is
    // "Browse all parts for [Vehicle]" -> goes directly to the bike's
    // collection page. Secondary action "or pick a different motorcycle"
    // re-opens the picker. When no vehicle, the only CTA is to pick one.
    var pickBikeHtml;
    if (hasVehicle) {
      var bikeName = escapeHtml(v.brand) + (v.model ? ' ' + escapeHtml(v.model) : '');
      var collectionUrl = getVehicleCollectionUrl();
      pickBikeHtml = ''
        + '<a class="eauto-pred__pick-bike-cta eauto-pred__pick-bike-cta--vehicle-set" href="' + collectionUrl + '">'
        +   '<span class="eauto-pred__pick-bike-text">'
        +     '<span class="eauto-pred__pick-bike-label">Browse all parts for</span>'
        +     '<span class="eauto-pred__pick-bike-bike">' + bikeName + '</span>'
        +   '</span>'
        +   '<span class="eauto-pred__pick-bike-arrow" aria-hidden="true">&rarr;</span>'
        + '</a>'
        + '<button type="button" class="eauto-pred__change-bike-link" data-eauto-empty-pick-bike>'
        +   '<span aria-hidden="true">&#8634;</span> Pick a different motorcycle'
        + '</button>';
    } else {
      pickBikeHtml = '<button type="button" class="eauto-pred__pick-bike-cta" data-eauto-empty-pick-bike>'
        + '<span class="eauto-pred__pick-bike-text">'
        +   '<span class="eauto-pred__pick-bike-label">Find parts for your bike</span>'
        +   '<span class="eauto-pred__pick-bike-sub">Tap to pick your brand &amp; model</span>'
        + '</span>'
        + '<span class="eauto-pred__pick-bike-arrow" aria-hidden="true">&rarr;</span>'
        + '</button>';
    }

    var chipsHtml = EMPTY_STATE_CATEGORIES.map(function (c) {
      return '<a class="eauto-pred__category-chip" data-eauto-empty-category="' + escapeHtml(c.q) + '" href="' + buildSearchUrl(c.q) + '">'
        + '<span class="eauto-pred__category-icon" aria-hidden="true">' + c.svg() + '</span>'
        + '<span class="eauto-pred__category-label">' + escapeHtml(c.label) + '</span>'
        + '</a>';
    }).join('');

    // Recent searches (if any) - rendered as data-eauto-sugg pills so they
    // reuse the existing click delegation AND keyboard navigation for free.
    var recents = getRecents();
    var recentsHtml = '';
    if (recents.length) {
      recentsHtml = ''
        + '<div class="eauto-pred__recents-head">'
        +   '<p class="eauto-pred__section-label">Recent searches</p>'
        +   '<button type="button" class="eauto-pred__recents-clear" data-eauto-recents-clear>Clear</button>'
        + '</div>'
        + '<div class="eauto-pred__term-chips">'
        +   recents.map(function (t) {
              return '<button type="button" class="eauto-pred__term-chip" data-eauto-sugg="' + escapeHtml(t) + '" role="option" tabindex="-1">'
                + '<span class="eauto-pred__term-chip-icon" aria-hidden="true">&#8634;</span>'
                + '<span class="eauto-pred__term-chip-label">' + escapeHtml(t) + '</span>'
                + '</button>';
            }).join('')
        + '</div>';
    }

    // Popular/trending searches - curated, always shown.
    var trendingHtml = ''
      + '<p class="eauto-pred__section-label">Popular searches</p>'
      + '<div class="eauto-pred__term-chips">'
      +   TRENDING_SEARCHES.map(function (t) {
            return '<button type="button" class="eauto-pred__term-chip" data-eauto-sugg="' + escapeHtml(t) + '" role="option" tabindex="-1">'
              + '<span class="eauto-pred__term-chip-icon" aria-hidden="true">' + svgIcon('search') + '</span>'
              + '<span class="eauto-pred__term-chip-label">' + escapeHtml(t) + '</span>'
              + '</button>';
          }).join('')
      + '</div>';

    inner.innerHTML = ''
      + '<div class="eauto-pred__empty-panel">'
      +   pickBikeHtml
      +   recentsHtml
      +   trendingHtml
      +   '<p class="eauto-pred__section-label">Popular categories</p>'
      +   '<div class="eauto-pred__category-chips">' + chipsHtml + '</div>'
      +   '<div class="eauto-pred__empty-footer">'
      +     '<p class="eauto-pred__empty-hint">Don\'t see your part? Type any part name in the search above.</p>'
      +     '<a class="eauto-pred__empty-browse-all" href="/collections">View all collections &rarr;</a>'
      +   '</div>'
      + '</div>';

    // Wire pick-bike CTA - programmatically click the vehicle filter toggle
    // (which also closes the dropdown via the existing handler, then opens
    // the brand/model panel above).
    var pickBtn = $('[data-eauto-empty-pick-bike]', inner);
    if (pickBtn) {
      pickBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var toggle = $('[data-eauto-veh-toggle]');
        if (toggle) toggle.click();
      });
    }
    // Category chips already have href via buildSearchUrl - click works natively.

    // Clear-recents button.
    var clearBtn = $('[data-eauto-recents-clear]', inner);
    if (clearBtn) {
      clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        clearRecents();
        renderEmptyState(); // re-render without the recents block
      });
    }

    // Fresh panel - reset keyboard selection so ArrowDown starts at the top.
    state.activeIndex = -1;
  }

  // Dropdown empty state - when the predictive search returns 0 products,
  // show the SAME enhanced layout as the /search page (browse-by-vehicle +
  // browse-by-category CTAs + category chips), just compact (6 chips).
  // Reuses buildZeroResultsHtml so the two surfaces stay visually consistent.
  function renderEmpty(inner, query, fallbackUsedNoVehicle) {
    var v = state.activeVehicle;
    var hasVehicle = !!(v && v.brand);
    var bikeName = hasVehicle ? v.brand + (v.model ? ' ' + v.model : '') : '';
    var html = buildZeroResultsHtml({
      term: query,
      bikeName: bikeName,
      hasVehicle: hasVehicle,
      chipsLimit: 6,
      variant: 'dropdown'
    });
    inner.innerHTML = html;
  }

  // Positive-only fitment badge: when a vehicle is selected and a product's
  // title contains that bike's model, show a green "Fits your X" badge.
  // Ambiguous / universal parts get NO badge - we never wrongly imply a part
  // does NOT fit. Gated by CONFIG.showFitmentBadges for one-line rollback.
  function fitmentBadge(p) {
    if (!CONFIG.showFitmentBadges) return '';
    var v = state.activeVehicle;
    if (!v || !v.model) return '';
    var title = ((p && p.title) || '').toLowerCase();
    if (!title) return '';
    var model = v.model.toLowerCase();
    var hit = title.indexOf(model) !== -1 ||
              title.replace(/\s+/g, '').indexOf(model.replace(/\s+/g, '')) !== -1;
    if (!hit) return '';
    return '<span class="eauto-pred__fitment" title="Fits your ' + escapeHtml(v.model) + '">'
      + '<svg viewBox="0 0 20 20" width="11" height="11" fill="currentColor" aria-hidden="true"><path d="M8.3 13.8 4.2 9.7l1.4-1.4 2.7 2.7 5.5-5.5 1.4 1.4z"/></svg>'
      + ' Fits your ' + escapeHtml(v.model) + '</span>';
  }

  function renderResults(inner, data, userQuery) {
    state.currentResults = data.products;
    var html = '';

    // T2.1 - "Browse all parts for [model] ->" smart redirect row.
    // Fires when the user's query exactly matches a bike model name (after
    // stripping common qualifiers like "spare parts"). Goes straight to the
    // model's collection page - bypasses the noisy /search results page.
    var modelMatch = findCollectionForModel(userQuery);
    if (modelMatch) {
      // Route to TEXT SEARCH results for the model (not the collection) - same
      // product decision as getVehicleCollectionUrl. modelMatch is still used to
      // detect the model-only query and supply a clean display label.
      var modelSearchUrl = CONFIG.searchPageUrl + '?q=' + encodeURIComponent(bestSearchUrlQuery(userQuery)) + '&type=product';
      // data-eauto-product-url makes this row participate in keyboard navigation
      // (arrow keys + Enter) - it's the first stop when arrowing down from input.
      html += '<a class="eauto-pred__model-redirect" href="' + escapeHtml(modelSearchUrl) + '"'
        + ' data-eauto-product-url="' + escapeHtml(modelSearchUrl) + '"'
        + ' role="option" tabindex="-1" data-no-instant>'
        + '  <span class="eauto-pred__model-redirect-icon" aria-hidden="true">&#127181;&#65039;</span>'
        + '  <span class="eauto-pred__model-redirect-text">'
        + '    <span class="eauto-pred__model-redirect-eyebrow">Browse all parts for</span>'
        + '    <span class="eauto-pred__model-redirect-title">' + escapeHtml(modelMatch.display) + '</span>'
        + '  </span>'
        + '  <span class="eauto-pred__model-redirect-arrow" aria-hidden="true">&rarr;</span>'
        + '</a>';
    }

    // T1.1 - Typo-correction notice: show *before* any vehicle-drop notice so
    // users see "Did you mean X?" as the primary explanation when relevant.
    if (data.correctedFrom && data.correctedTo) {
      html += '<p class="eauto-pred__notice">Showing results for <strong>'
        + escapeHtml(data.correctedTo) + '</strong>. '
        + 'Search instead for <a href="' + CONFIG.searchPageUrl
        + '?q=' + encodeURIComponent(data.correctedFrom) + '&type=product" '
        + 'class="eauto-pred__notice-link">'
        + escapeHtml(data.correctedFrom) + '</a>?</p>';
    }

    if (data.fallbackUsedNoVehicle && state.activeVehicle) {
      html += '<p class="eauto-pred__notice">No matches for <strong>' + escapeHtml(state.activeVehicle.brand)
        + (state.activeVehicle.model ? ' ' + escapeHtml(state.activeVehicle.model) : '')
        + '</strong>. Showing all matches.</p>';
    }

    if (data.suggestions.length) {
      html += '<ul class="eauto-pred__suggestions list--unstyled" role="listbox" aria-label="Search suggestions">';
      data.suggestions.forEach(function (s, i) {
        var styled = s.styled_text || escapeHtml(s.text);
        html += '<li role="option" data-eauto-sugg="' + escapeHtml(s.text) + '" tabindex="-1">'
          + '<span class="eauto-pred__sugg-icon" aria-hidden="true">' + svgIcon('search') + '</span>'
          + styled
          + '</li>';
      });
      html += '</ul>';
    }

    if (data.products.length) {
      html += '<ul class="eauto-pred__products list--unstyled" role="listbox" aria-label="Product matches">';
      data.products.forEach(function (p, i) {
        // /search/suggest.json returns featured_image as a STRING url, not an object.
        var img = '';
        if (typeof p.featured_image === 'string') img = p.featured_image;
        else if (p.featured_image && p.featured_image.url) img = p.featured_image.url;
        else if (p.image) img = p.image;

        var priceNum = parseFloat(p.price);
        var price = isNaN(priceNum) ? '' : formatMoney(priceNum);

        // Field is `compare_at_price` on the suggest endpoint (not _min/_max).
        var capRaw = p.compare_at_price || p.compare_at_price_min;
        var capNum = capRaw ? parseFloat(capRaw) : 0;
        var compareAt = (capNum && capNum > priceNum) ? formatMoney(capNum) : '';
        html += '<li role="option" data-eauto-product-url="' + escapeHtml(p.url) + '" data-index="' + (data.suggestions.length + i) + '" tabindex="-1">'
          + '<a href="' + escapeHtml(p.url) + '" class="eauto-pred__product-link" data-no-instant>'
          + '  <span class="eauto-pred__thumb">'
          + (img ? '<img src="' + escapeHtml(img) + '" alt="" loading="lazy" width="56" height="56" />' : '<span class="eauto-pred__thumb-fallback" aria-hidden="true">' + svgIcon('image') + '</span>')
          + '  </span>'
          + '  <span class="eauto-pred__product-info">'
          + '    <span class="eauto-pred__product-title">' + highlightMatch(p.title, userQuery) + '</span>'
          + (p.vendor ? '    <span class="eauto-pred__product-vendor">' + escapeHtml(p.vendor) + '</span>' : '')
          + '    <span class="eauto-pred__product-price">'
          +        '<span class="eauto-pred__price-now">' + price + '</span>'
          +        (compareAt ? ' <span class="eauto-pred__price-was">' + compareAt + '</span>' : '')
          +     '</span>'
          +     fitmentBadge(p)
          + '  </span>'
          + '</a>'
          + '</li>';
      });
      html += '</ul>';
      html += '<a class="eauto-pred__see-all" href="' + buildSearchUrl(userQuery) + '" data-no-instant>See all results for "' + escapeHtml(userQuery) + '" &rarr;</a>';
    } else if (!data.suggestions.length && !modelMatch) {
      // No products, no suggestions, no model redirect - fall back to empty state.
      renderEmpty(inner, userQuery, data.fallbackUsedNoVehicle);
      return;
    } else if (!data.suggestions.length && modelMatch) {
      // Only model redirect - give users an escape hatch to /search too.
      html += '<a class="eauto-pred__see-all" href="' + CONFIG.searchPageUrl
        + '?q=' + encodeURIComponent(userQuery) + '&type=product" data-no-instant>'
        + 'Or browse search results for "' + escapeHtml(userQuery) + '" &rarr;</a>';
    }

    inner.innerHTML = html;
  }

  function svgIcon(name) {
    if (name === 'search') {
      return '<svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M12.9 14.32a8 8 0 1 1 1.41-1.41l4.39 4.4-1.41 1.4-4.4-4.39zM8 14a6 6 0 1 0 0-12 6 6 0 0 0 0 12z"/></svg>';
    }
    if (name === 'image') {
      return '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>';
    }
    if (name === 'close') {
      return '<svg viewBox="0 0 20 20" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M10 8.586L4.95 3.536 3.536 4.95 8.586 10l-5.05 5.05 1.414 1.414L10 11.414l5.05 5.05 1.414-1.414L11.414 10l5.05-5.05L15.05 3.536z"/></svg>';
    }
    if (name === 'chevron') {
      return '<svg viewBox="0 0 20 20" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/></svg>';
    }
    return '';
  }

  // ---------------------------------------------------------------------------
  // OPEN / CLOSE
  // ---------------------------------------------------------------------------
  function openDropdown() {
    var box = $(CONFIG.selectors.resultsBox);
    if (!box) return;
    box.setAttribute('aria-hidden', 'false');
    box.classList.add('is-open');
    state.isOpen = true;
  }

  function closeDropdown() {
    var box = $(CONFIG.selectors.resultsBox);
    if (!box) return;
    box.setAttribute('aria-hidden', 'true');
    box.classList.remove('is-open');
    state.isOpen = false;
    state.activeIndex = -1;
  }

  // ---------------------------------------------------------------------------
  // VEHICLE FILTER UI
  // ---------------------------------------------------------------------------
  function renderVehicleFilter() {
    var mount = $(CONFIG.selectors.vehicleMount);
    if (!mount) return;

    var brands = Object.keys(VEHICLES);
    var sel = state.activeVehicle || {};

    var html = ''
      + '<div class="eauto-veh' + (sel.brand ? ' eauto-veh--has-vehicle' : '') + '">'
      + '  <button type="button" class="eauto-veh__chip" data-eauto-veh-toggle aria-haspopup="true" aria-expanded="false">'
      + '    <span class="eauto-veh__chip-icon" aria-hidden="true">' + svgMotorcycle() + '</span>'
      + '    <span class="eauto-veh__chip-label">'
      + (sel.brand ? escapeHtml(sel.brand) + (sel.model ? ' &middot; ' + escapeHtml(sel.model) : '') : 'Select Motorcycle')
      + '    </span>'
      + '    <span class="eauto-veh__chip-caret" aria-hidden="true">' + svgIcon('chevron') + '</span>'
      + '  </button>'
      + (sel.brand
        ? '  <button type="button" class="eauto-veh__clear" data-eauto-veh-clear aria-label="Clear vehicle filter">' + svgIcon('close') + '</button>'
        : '')
      + '  <div class="eauto-veh__panel" data-eauto-veh-panel hidden>'
      + '    <div class="eauto-veh__col">'
      + '      <p class="eauto-veh__col-title">Brand</p>'
      + '      <ul class="eauto-veh__list list--unstyled" data-eauto-veh-brands>'
      +          brands.map(function (b) {
                  return '<li><button type="button" data-brand="' + escapeHtml(b) + '" class="eauto-veh__item' + (sel.brand === b ? ' is-active' : '') + '">' + escapeHtml(b) + '</button></li>';
                }).join('')
      + '      </ul>'
      + '    </div>'
      + '    <div class="eauto-veh__col" data-eauto-veh-models-col>'
      + '      <p class="eauto-veh__col-title">Model</p>'
      + '      <ul class="eauto-veh__list list--unstyled" data-eauto-veh-models>'
      + (sel.brand
          ? VEHICLES[sel.brand].map(function (m) {
              return '<li><button type="button" data-model="' + escapeHtml(m) + '" class="eauto-veh__item' + (sel.model === m ? ' is-active' : '') + '">' + escapeHtml(m) + '</button></li>';
            }).join('')
          : '<li class="eauto-veh__hint">Pick a brand first</li>')
      + '      </ul>'
      + '    </div>'
      + '  </div>'
      + '</div>';

    mount.innerHTML = html;
    wireVehicleFilter(mount);
  }

  function wireVehicleFilter(root) {
    var toggle = $('[data-eauto-veh-toggle]', root);
    var panel = $('[data-eauto-veh-panel]', root);
    var clearBtn = $('[data-eauto-veh-clear]', root);

    if (toggle) {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        var isHidden = panel.hasAttribute('hidden');
        if (isHidden) {
          panel.removeAttribute('hidden');
          // Close the search dropdown so it doesn't z-index-overlay the
          // vehicle panel. The two UIs should be mutually exclusive - the
          // user is either picking a vehicle or browsing search results.
          closeDropdown();
        } else {
          panel.setAttribute('hidden', '');
        }
        toggle.setAttribute('aria-expanded', isHidden ? 'true' : 'false');
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        state.activeVehicle = null;
        safeStorage('del', CONFIG.storageKey);
        renderVehicleFilter();
        var input = $(CONFIG.selectors.input);
        if (input && input.value.trim().length >= CONFIG.minChars) {
          onInput({ target: input });
        }
      });
    }

    $$('[data-brand]', root).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation(); // prevent the module-level outside-click from firing
        var brand = btn.getAttribute('data-brand');
        state.activeVehicle = { brand: brand, model: null };
        safeStorage('set', CONFIG.storageKey, JSON.stringify(state.activeVehicle));
        renderVehicleFilter();
        // Re-open the panel so user can pick a model
        var freshPanel = $('[data-eauto-veh-panel]');
        var freshToggle = $('[data-eauto-veh-toggle]');
        if (freshPanel) freshPanel.removeAttribute('hidden');
        if (freshToggle) freshToggle.setAttribute('aria-expanded', 'true');
      });
    });

    $$('[data-model]', root).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (!state.activeVehicle) return;
        state.activeVehicle.model = btn.getAttribute('data-model');
        safeStorage('set', CONFIG.storageKey, JSON.stringify(state.activeVehicle));
        renderVehicleFilter();
        var input = $(CONFIG.selectors.input);
        if (input) {
          input.focus();
          if (input.value.trim().length >= CONFIG.minChars) {
            clearTimeout(state.debounceId); // cancel pending debounce
            onInput({ target: input });
          }
        }
      });
    });
  }

  // Single module-level outside-click handler - attached once in init().
  // Always queries the live DOM so panel re-renders don't strand listeners.
  function onDocClickForVehiclePanel(e) {
    var root = $(CONFIG.selectors.vehicleMount);
    if (!root || root.contains(e.target)) return;
    var panel = $('[data-eauto-veh-panel]', root);
    var toggle = $('[data-eauto-veh-toggle]', root);
    if (panel && !panel.hasAttribute('hidden')) {
      panel.setAttribute('hidden', '');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }
  }

  // ---------------------------------------------------------------------------
  // INPUT HANDLING
  // ---------------------------------------------------------------------------
  function onInput(e) {
    var q = (e.target.value || '').trim();
    var inner = $(CONFIG.selectors.resultsInner);
    if (!inner) return;

    if (q.length < CONFIG.minChars) {
      // NEW: don't close the dropdown - render the empty state so customers
      // see the vehicle CTA + category chips instead of a blank panel.
      renderEmptyState();
      openDropdown();
      return;
    }

    openDropdown();
    showSkeleton(inner);

    runMultiQuery(q).then(function (data) {
      if (data._stale) return; // a newer query has already taken over
      // Analytics: one event per settled query (deduped against the last).
      if (q && q !== _saLastQuery) {
        _saLastQuery = q;
        emitSA('eauto:search', Object.assign({
          raw_query: q,
          norm_query: (typeof normalizeQuery === 'function') ? normalizeQuery(q) : q,
          corrected_from: data.correctedFrom || null,
          corrected_to: data.correctedTo || null,
          result_count: data.products ? data.products.length : 0,
          zero_result: (data.products && data.products.length) ? 0 : 1,
          fallback_path: data.fallbackUsedNoVehicle ? 'fb-drop-vehicle' : (data.correctedTo ? 'fb-typo' : 'primary')
        }, saVehicle()));
      }
      // T2.1 - if the query matches a known bike model, ALWAYS go through
      // renderResults so the "Browse all parts for X" redirect row is shown,
      // even when the API returned zero products/suggestions.
      var hasModelMatch = !!findCollectionForModel(q);
      if (!hasModelMatch && !data.products.length && !data.suggestions.length) {
        renderEmpty(inner, q, data.fallbackUsedNoVehicle);
      } else {
        renderResults(inner, data, q);
      }
    }).catch(function (err) {
      if (err && err.name === 'AbortError') return; // expected on rapid typing
      renderEmpty(inner, q, false);
    });
  }

  // ---------------------------------------------------------------------------
  // KEYBOARD NAV
  // ---------------------------------------------------------------------------
  function onKeyDown(e) {
    if (!state.isOpen) return;
    var items = $$('[data-eauto-sugg], [data-eauto-product-url]', $(CONFIG.selectors.resultsInner));
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      state.activeIndex = Math.min(items.length - 1, state.activeIndex + 1);
      updateActive(items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      state.activeIndex = Math.max(0, state.activeIndex - 1);
      updateActive(items);
    } else if (e.key === 'Enter') {
      var active = items[state.activeIndex];
      if (active) {
        e.preventDefault();
        if (active.hasAttribute('data-eauto-product-url')) {
          window.location.href = active.getAttribute('data-eauto-product-url');
        } else if (active.hasAttribute('data-eauto-sugg')) {
          // Navigate directly - don't rely on form.submit() which would bypass
          // some theme intercepts and may use the wrong form when the mobile
          // drawer is open. buildSearchUrl preserves vehicle filter context.
          var sugg = active.getAttribute('data-eauto-sugg');
          pushRecent(sugg);
          window.location.href = buildSearchUrl(sugg);
        }
      }
    } else if (e.key === 'Escape') {
      closeDropdown();
    }
  }

  function updateActive(items) {
    items.forEach(function (el, i) {
      if (i === state.activeIndex) {
        el.classList.add('is-active');
        el.setAttribute('aria-selected', 'true');
        if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      } else {
        el.classList.remove('is-active');
        el.removeAttribute('aria-selected');
      }
    });
  }

  // ---------------------------------------------------------------------------
  // INIT
  // ---------------------------------------------------------------------------
  function init() {
    var input = $(CONFIG.selectors.input);
    var form = $(CONFIG.selectors.form);
    if (!input || !form) {
      // Header markup not present on this page (e.g., checkout). Bail silently.
      return;
    }

    // Maestrooo's theme.min.js predictive search is active and writes to
    // .search-bar__results-inner asynchronously. If we render into the same
    // container, Maestrooo overwrites us. So we create our OWN container
    // (.eauto-pred-container) and hide Maestrooo's .search-bar__results
    // entirely via CSS. This avoids the DOM conflict cleanly.
    var container = form.querySelector('.eauto-pred-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'eauto-pred-container';
      container.setAttribute('aria-hidden', 'true');
      form.appendChild(container);
    }
    // Override selectors so all later $(CONFIG.selectors.resultsBox/Inner)
    // calls find our container, not Maestrooo's.
    CONFIG.selectors.resultsBox = '.eauto-pred-container';
    CONFIG.selectors.resultsInner = '.eauto-pred-container';
    var box = container;
    var inner = container;

    // Restore vehicle from storage
    var saved = safeStorage('get', CONFIG.storageKey);
    if (saved) {
      try { state.activeVehicle = JSON.parse(saved); } catch (e) {}
    }

    // Hide the legacy skeleton (it's left over from Maestrooo's broken JS)
    var skeleton = $(CONFIG.selectors.skeleton);
    if (skeleton) skeleton.style.display = 'none';

    // Pull dashboard-managed config (cached locally; revalidated in background).
    loadRemoteConfig();

    renderVehicleFilter();

    // Two-tier input handling:
    //   - Immediate (non-debounced): show skeleton OR empty state so the user
    //     sees feedback within one frame of typing. Fixes the "feels laggy"
    //     impression that came from waiting 200ms before any UI changed.
    //   - Debounced: actually run the network search 200ms later.
    var debounced = debounce(onInput, CONFIG.debounceMs);
    input.addEventListener('input', function (e) {
      var q = (e.target.value || '').trim();
      var inner = $(CONFIG.selectors.resultsInner);
      if (!inner) return;
      var formEl = $(CONFIG.selectors.form);
      // Toggle `eauto-typing` class on the form so CSS can collapse the
      // vehicle chip + Close button on mobile, giving the input more width.
      if (formEl) formEl.classList.toggle('eauto-typing', q.length > 0);
      if (q.length < CONFIG.minChars) {
        // Empty/short query - show empty-state panel (vehicle CTA + categories).
        // No fetch needed, but cancel any in-flight debounce.
        clearTimeout(state.debounceId);
        renderEmptyState();
        openDropdown();
        return;
      }
      // Query has content - show skeleton immediately for visual feedback,
      // then let the debounced fetch run.
      openDropdown();
      showSkeleton(inner);
      debounced(e);
    });
    input.addEventListener('focus', function (e) {
      var val = (e.target.value || '').trim();
      if (val.length >= CONFIG.minChars) {
        openDropdown();
      } else {
        // Show empty-state panel even on first focus - customers tapping the
        // search bar with no query should see vehicle CTA + categories, not
        // a blank/closed dropdown.
        renderEmptyState();
        openDropdown();
      }
    });
    input.addEventListener('keydown', onKeyDown);

    // Click suggestion / product
    inner.addEventListener('click', function (e) {
      var sugg = e.target.closest('[data-eauto-sugg]');
      if (sugg) {
        e.preventDefault();
        var text = sugg.getAttribute('data-eauto-sugg');
        pushRecent(text);
        emitSA('eauto:searchclick', Object.assign({
          raw_query: text, action: 'click_suggestion', action_target: text
        }, saVehicle()));
        // buildSearchUrl handles both the query expansion AND prepending
        // vehicle tags if a vehicle is currently selected.
        window.location.href = buildSearchUrl(text);
        return;
      }
    });

    // Close on outside click (single listener, attached once)
    document.addEventListener('click', function (e) {
      var form = $(CONFIG.selectors.form);
      if (form && !form.contains(e.target)) closeDropdown();
    });

    // Vehicle-panel outside-click handler - single registration
    document.addEventListener('click', onDocClickForVehiclePanel);

    // Mark the form's existing input-clear button to also close the dropdown
    var clearBtn = $('.search-bar__input-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        closeDropdown();
        input.focus();
      });
    }

    // Form-submit interceptor - handles three cases:
    //   1) Empty input + no vehicle: short-circuit, don't ship users to the
    //      dead /search?q= page (which had 185 landings in 90 days of analytics)
    //   2) Vehicle selected (with or without typed text): prepend tag: filters
    //      to q so the /search results page is also scoped to the vehicle
    //   3) Typed text + no vehicle: let the form submit normally
    var form = $(CONFIG.selectors.form);
    if (form) {
      form.addEventListener('submit', function (e) {
        var typed = (input.value || '').trim();

        // Case 1 - empty + no vehicle: cancel the submit and keep focus on the input.
        if (!typed && !state.activeVehicle) {
          e.preventDefault();
          input.focus();
          return;
        }

        // Cases 2 + 3 - either vehicle selected, or typed text alone, or
        // both. buildSearchUrl handles all of: vehicle-tag prepend (if
        // selected), best-expanded query form, and URL composition.
        // We preventDefault + navigate so input.value isn't mutated mid-submit.
        e.preventDefault();
        if (typed) pushRecent(typed);
        window.location.href = buildSearchUrl(typed);
      });
    }
  }

  // ---------------------------------------------------------------------------
  // SEARCH RESULTS PAGE - prettify visible text
  // ---------------------------------------------------------------------------
  // When the form submits with a vehicle filter active, the URL contains
  //   q=tag:"VehicleBrand_Bajaj" tag:"VehicleModel_Pulsar 150" brake
  // which Shopify echoes verbatim into the page heading, breadcrumb, document
  // title, and the search input. We need to replace every visible occurrence
  // of `tag:"VehicleBrand_X"` / `tag:"VehicleModel_X"` with the friendly name.
  //
  // Approach: walk all text nodes in the body and substitute. Also fix the
  // <input value="..."> separately because input values are not text nodes.
  // ---------------------------------------------------------------------------
  // SEARCH RESULTS COUNT BRACKETING (T3.1.2)
  // ---------------------------------------------------------------------------
  // On /search?q=... results pages, the Maestrooo theme prints "X results"
  // (e.g. "73 results"). Showing exact counts reveals our catalog depth to
  // competitors. Replace with bucketed values like "50+", "100+" - small
  // counts (<=5) stay exact so customers don't feel misled.
  function bracketCount(n) {
    if (!isFinite(n) || n <= 5) return n;
    var buckets = [10, 25, 50, 100, 250, 500, 1000, 2500, 5000];
    var pick = 5;
    for (var i = 0; i < buckets.length; i++) {
      if (n >= buckets[i]) pick = buckets[i];
    }
    return pick + '+';
  }
  function bracketSearchResultsCount() {
    try {
      if (location.pathname !== '/search' && !/^\/collections\//.test(location.pathname)) return;
      // Handle counts with commas: 1,085 / 1,234,567
      var simpleRx = /\b([\d,]+)(\s+(?:results?|products?|items?))\b/i;
      var rangeRx = /Showing\s+[\d,]+\s*-\s*[\d,]+\s+of\s+([\d,]+)\s+(results?|products?|items?)/i;
      var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
        acceptNode: function (n) {
          if (!n.nodeValue || n.nodeValue.length > 200) return NodeFilter.FILTER_REJECT;
          return simpleRx.test(n.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        }
      });
      var nodes = [];
      var node;
      while ((node = walker.nextNode())) nodes.push(node);
      nodes.forEach(function (n) {
        // Handle range "Showing 1 - 24 of 1,085 results" first.
        var fullMatch = n.nodeValue.match(rangeRx);
        if (fullMatch) {
          var total = parseInt(fullMatch[1].replace(/,/g, ''), 10);
          n.nodeValue = n.nodeValue.replace(fullMatch[0], 'Showing ' + bracketCount(total) + ' ' + fullMatch[2]);
          return;
        }
        // Simple "1,085 results" -> "1000+ results"
        n.nodeValue = n.nodeValue.replace(simpleRx, function (m, num, suffix) {
          return bracketCount(parseInt(num.replace(/,/g, ''), 10)) + suffix;
        });
      });
    } catch (e) {
      if (window.console && console.warn) console.warn('eauto bracket count:', e);
    }
  }

  // ---------------------------------------------------------------------------
  // ZERO-RESULTS PAGE ENHANCEMENT (T2.4)
  // ---------------------------------------------------------------------------
  // Replace Maestrooo's plain "No results could be found for X" page with a
  // friendlier panel: headline + two fallback CTAs (browse vehicle / browse
  // category) + category chip grid. Reuses helpers we already have.
  //
  // Detects zero-results via DOM text match. Walks up to find the section
  // container and replaces its content. Idempotent - re-running does nothing.
  function enhanceZeroResultsPage() {
    try {
      if (location.pathname !== '/search') return;
      // Idempotent - if we already enhanced, skip
      if (document.querySelector('.eauto-zero-results--page')) return;

      // PRECISE TARGETING: Maestrooo's Warehouse theme renders the search
      // empty state with class="empty-state" inside section data-section-type="collection".
      // We replace that element's innerHTML directly - no DOM walking,
      // no hiding siblings, no guesswork.
      var emptyState = document.querySelector('.empty-state');
      if (!emptyState) return; // not on a zero-results page

      // Sanity check - make sure this is the search empty state, not some
      // other "empty-state" element on the page.
      var noResultsRegex = /no\s+(matching\s+)?(results|products|items)\b|could\s+not\s+be\s+found|no\s+results\s+could\s+be\s+found/i;
      if (!noResultsRegex.test(emptyState.textContent || '')) return;

      // Restore state.activeVehicle from localStorage if init() hasn't run
      // yet - enhanceZeroResultsPage often runs before init in the load order.
      // Without this, headlines lose the bike-name context on direct page loads.
      if (!state.activeVehicle) {
        var saved = safeStorage('get', CONFIG.storageKey);
        if (saved) {
          try { state.activeVehicle = JSON.parse(saved); } catch (e) {}
        }
      }

    // Parse URL: split tag: prefixes from the search term
    var params = new URLSearchParams(location.search);
    var rawQ = (params.get('q') || '').trim();
    var tagBrand = null, tagModel = null;
    var term = rawQ
      // Parse legacy tag: format (for URLs from old paste) - kept for backward compat
      .replace(/tag:"VehicleBrand_([^"]+)"\s*/i, function (m, b) { tagBrand = b; return ''; })
      .replace(/tag:"VehicleModel_([^"]+)"\s*/i, function (m, m2) { tagModel = m2; return ''; })
      .trim();
    // Vehicle context - prefer URL tags (legacy), fall back to state.activeVehicle
    // (the current text-based vehicle prefix doesn't appear in URL as parseable tokens).
    var bikeBrand = tagBrand || (state.activeVehicle && state.activeVehicle.brand) || '';
    var bikeModel = tagModel || (state.activeVehicle && state.activeVehicle.model) || '';
    var hasVehicle = !!bikeBrand;
    var bikeName = hasVehicle ? bikeBrand + (bikeModel ? ' ' + bikeModel : '') : '';

    // For text-based URLs (current format), the bike name is prefixed as plain
    // text at the START of the query string. Strip it from the term so the
    // headline reads "We don't have <term> for <bike>" not "We don't have
    // <bike + term> for <bike>".
    if (hasVehicle && !tagBrand && bikeName) {
      var bikePrefixRx = new RegExp('^\\s*' + bikeName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') + '\\s*', 'i');
      term = term.replace(bikePrefixRx, '').trim();
    }

    // Restore the active-vehicle state so chip / chips / buildSearchUrl all
    // produce vehicle-aware URLs even on a fresh page load (direct link).
    if (hasVehicle && !state.activeVehicle) {
      state.activeVehicle = { brand: bikeBrand, model: bikeModel };
    }

      // Build the enhanced inner HTML (use 12 chips on the /search page -
      // unlike the dropdown which is space-constrained).
      var html = buildZeroResultsHtml({
        term: term,
        bikeName: bikeName,
        hasVehicle: hasVehicle,
        chipsLimit: 12,
        variant: 'page'
      });

      // Direct replace - Maestrooo's `.empty-state` has the heading, "No
      // results" message, secondary search box, and back-home link as
      // children. Replacing its innerHTML wipes all of those cleanly.
      emptyState.innerHTML = html;
      emptyState.classList.add('eauto-zero-results--page');
    } catch (e) {
      if (window.console && console.warn) console.warn('eauto zero-results:', e);
    }
  }

  // Shared builder used by both /search-page enhancement and dropdown empty
  // state. Takes { term, bikeName, hasVehicle, chipsLimit, variant }.
  function buildZeroResultsHtml(opts) {
    var term = opts.term, bikeName = opts.bikeName, hasVehicle = opts.hasVehicle;
    var chipsLimit = opts.chipsLimit || 6;
    var variant = opts.variant || 'dropdown';

    var html = '<div class="eauto-zero-results eauto-zero-results--' + variant + '">';

    // Headline
    if (hasVehicle && term) {
      html += '<h2 class="eauto-zero-results__heading">We don\'t have <strong>' + escapeHtml(term) + '</strong> for <strong>' + escapeHtml(bikeName) + '</strong> right now.</h2>';
    } else if (term) {
      html += '<h2 class="eauto-zero-results__heading">We don\'t have <strong>' + escapeHtml(term) + '</strong> in stock right now.</h2>';
    } else if (hasVehicle) {
      html += '<h2 class="eauto-zero-results__heading">No matching parts for <strong>' + escapeHtml(bikeName) + '</strong>.</h2>';
    } else {
      html += '<h2 class="eauto-zero-results__heading">No matching parts found.</h2>';
    }

    // CTAs
    html += '<div class="eauto-zero-results__ctas">';
    if (hasVehicle) {
      html += '<a class="eauto-zero-results__primary-cta" href="' + getVehicleCollectionUrl() + '">'
        + 'Browse all ' + escapeHtml(bikeName) + ' parts &rarr;'
        + '</a>';
    }
    if (term) {
      var bareTermUrl = CONFIG.searchPageUrl + '?q=' + encodeURIComponent(term) + '&type=product&_eauto_corr=1';
      html += '<a class="eauto-zero-results__secondary-cta" href="' + bareTermUrl + '">'
        + 'Browse all ' + escapeHtml(term) + ' &rarr;'
        + '</a>';
    }
    html += '</div>';

    // Category chips
    html += '<div class="eauto-zero-results__categories">'
      + '<p class="eauto-zero-results__category-label">'
      + (hasVehicle ? 'Other categories for ' + escapeHtml(bikeName) : 'Popular categories')
      + '</p>'
      + '<div class="eauto-pred__category-chips">';
    EMPTY_STATE_CATEGORIES.slice(0, chipsLimit).forEach(function (c) {
      html += '<a class="eauto-pred__category-chip" href="' + buildSearchUrl(c.q) + '">'
        + '<span class="eauto-pred__category-icon" aria-hidden="true">' + c.svg() + '</span>'
        + '<span class="eauto-pred__category-label">' + escapeHtml(c.label) + '</span>'
        + '</a>';
    });
    html += '</div></div></div>';
    return html;
  }

  function prettifySearchHeading() {
    if (!/^\/search/.test(window.location.pathname)) return;

    var qStr = '';
    try {
      qStr = new URLSearchParams(window.location.search).get('q') || '';
    } catch (e) { return; }

    var hasBrand = /tag:"VehicleBrand_/.test(qStr);
    var hasModel = /tag:"VehicleModel_/.test(qStr);
    if (!hasBrand && !hasModel) return;

    // Strip the tag: prefixes to recover the user's actual typed text
    var typed = qStr
      .replace(/tag:"VehicleBrand_[^"]+"\s*/g, '')
      .replace(/tag:"VehicleModel_[^"]+"\s*/g, '')
      .trim();

    // Substitution used everywhere: convert tag:"VehicleX_Name" -> Name
    function rewrite(text) {
      if (typeof text !== 'string' || text.indexOf('tag:') === -1) return text;
      return text
        .replace(/tag:"VehicleBrand_([^"]+)"\s*/g, '$1 ')
        .replace(/tag:"VehicleModel_([^"]+)"\s*/g, '$1 ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    // 1. Fix the search input - it shows q verbatim, including the tag prefix.
    //    Display only the user's typed text; the vehicle chip already
    //    communicates the active filter.
    var headerInput = $(CONFIG.selectors.input);
    if (headerInput && headerInput.value.indexOf('tag:') !== -1) {
      headerInput.value = typed;
    }

    // 2. Walk every text node in <body> and rewrite any tag:"Vehicle..." occurrence.
    //    Skip <script>, <style>, <textarea>, <noscript>.
    var walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function (node) {
          var p = node.parentElement;
          if (!p) return NodeFilter.FILTER_REJECT;
          var tag = p.tagName;
          if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEXTAREA' || tag === 'NOSCRIPT') {
            return NodeFilter.FILTER_REJECT;
          }
          return node.nodeValue.indexOf('tag:') === -1
            ? NodeFilter.FILTER_REJECT
            : NodeFilter.FILTER_ACCEPT;
        }
      }
    );
    var dirty = [];
    while (walker.nextNode()) dirty.push(walker.currentNode);
    dirty.forEach(function (node) { node.nodeValue = rewrite(node.nodeValue); });

    // 3. Fix any <input value="..."> or <input data-...="..."> that still has tag: in it.
    $$('input[value*="tag:"]').forEach(function (el) {
      el.value = rewrite(el.value);
    });

    // 4. Document title (browser tab)
    if (document.title.indexOf('tag:') !== -1) {
      document.title = rewrite(document.title);
    }
  }

  // ---------------------------------------------------------------------------
  // /search PAGE AUTO-REDIRECT (T2.3)
  // ---------------------------------------------------------------------------
  // When a customer lands on /search?q=<typo>, Shopify's server-side search
  // doesn't know our synonym dictionary, so they see "no products found" even
  // if our JS dict could rescue them.
  //
  // This function runs ASAP on page load: reads the q param, applies the same
  // bestSearchUrlQuery() helper the dropdown uses, and location.replace()s to
  // the corrected URL if different. Single round-trip, ~100ms perceived cost,
  // and the customer lands on the right products page.
  //
  // Architectural value: ONE source of truth (the SYNONYMS dict in this file)
  // now rescues both the dropdown AND the results page. No Shopify Search &
  // Discovery admin synonyms to maintain in parallel.
  //
  // Loop prevention: redirect URL adds _eauto_corr=1; we bail if it's already set.
  // Vehicle-tag queries (q="tag:..." prefixed) are skipped - those come from
  // our own dropdown navigation which already passes the corrected query.
  function tryRescueSearchPage() {
    try {
      if (location.pathname !== '/search') return;
      var params = new URLSearchParams(location.search);
      if (params.get('_eauto_corr') === '1') return; // already corrected once
      var q = (params.get('q') || '').trim();
      if (!q) return;
      if (q.indexOf('tag:') !== -1) return; // vehicle-filter query - leave alone
      var corrected = bestSearchUrlQuery(q);
      if (!corrected) return;
      // Case-insensitive compare so we don't redirect just to lowercase the
      // URL (e.g. "Brake" -> "brake" - same content, wasteful redirect).
      if (corrected.toLowerCase() === q.toLowerCase()) return;
      params.set('q', corrected);
      params.set('_eauto_corr', '1');
      // Use replace() so the typo URL isn't in browser history - clean back button.
      location.replace(location.pathname + '?' + params.toString());
    } catch (e) {
      // Never let a bug in the rescue logic block normal page rendering.
      if (window.console && console.warn) console.warn('eauto search rescue:', e);
    }
  }

  // Run BEFORE DOMContentLoaded - the redirect should fire as fast as possible,
  // ideally before the empty-results page paints.
  tryRescueSearchPage();

  // Run /search-page enhancements INDEPENDENTLY of init() so an error in one
  // doesn't block the others. Each is also wrapped in try/catch internally.
  function runSearchPageEnhancements() {
    try { prettifySearchHeading(); } catch (e) {}
    try { bracketSearchResultsCount(); } catch (e) {}
    try { enhanceZeroResultsPage(); } catch (e) {}
  }

  // Maestrooo's Warehouse theme uses Instantclick (note <body data-instant-intensity="viewport">).
  // Instantclick swaps DOM content via AJAX on navigation - DOMContentLoaded
  // only fires on the FIRST page load, never on subsequent in-app navigations.
  // We have to listen for the theme's custom navigation events too.
  function bindNavigationHooks() {
    // Standard browser navigation (back/forward)
    window.addEventListener('popstate', function () { setTimeout(runSearchPageEnhancements, 50); });
    // Shopify theme editor + section reloads
    document.addEventListener('shopify:section:load', runSearchPageEnhancements);
    // Instantclick fires a custom event on document when navigation completes.
    // Different builds use different names - listen to several.
    ['change', 'InstantClick:change', 'instantclick:change', 'instantclick:ready'].forEach(function (evt) {
      document.addEventListener(evt, function () { setTimeout(runSearchPageEnhancements, 50); }, false);
    });
    // Last-resort fallback: MutationObserver on body.children detects any
    // wholesale DOM swap (Instantclick, Turbo, custom AJAX, etc.). Debounced
    // so we don't thrash on minor re-renders.
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () { pending = false; runSearchPageEnhancements(); }, 100);
    }).observe(document.body, { childList: true, subtree: false });
  }

  // Multiple delayed retries - Instantclick / theme.min.js sometimes finishes
  // its DOM swap a few hundred ms after our listeners fire.
  function scheduleRetries() {
    setTimeout(runSearchPageEnhancements, 500);
    setTimeout(runSearchPageEnhancements, 1500);
    setTimeout(runSearchPageEnhancements, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      runSearchPageEnhancements();
      try { init(); } catch (e) { if (window.console && console.warn) console.warn('eauto init:', e); }
      bindNavigationHooks();
      scheduleRetries();
    });
  } else {
    runSearchPageEnhancements();
    try { init(); } catch (e) { if (window.console && console.warn) console.warn('eauto init:', e); }
    bindNavigationHooks();
    scheduleRetries();
  }
})();
